import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Search, Send, Pin, FileText, FolderOpen, Zap, ListTree, Bell, BellOff, Pencil, Ban, Check, CheckCheck, Mail, Languages, Reply as ReplyIcon, Clock, ChevronDown, ChevronUp, ChevronLeft, Users, CornerUpLeft, Loader2, X, Plus, HelpCircle, Bot } from 'lucide-react';
import { Tabs } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuSub, ContextMenuSubTrigger, ContextMenuSubContent, ContextMenuSeparator } from '../../components/ui/context-menu';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api, { apiError, extractFloodwait, timeAgo, mediaUrl, fmtDayKeyParis, fmtDayLabelParis, fmtDateTimeParis } from '../../lib/api';
import { LANGUAGES, tgStatus } from './inbox/utils';
import { MessageBubble } from './inbox/MessageBubble';
import { InsightPanel } from './inbox/InsightPanel';
import { RenameDialog, EmojiEditDialog, AddToListDialog, ReportSaleDialog, EMOJI_BANK } from './inbox/Dialogs';
import { ScriptsPickerDialog, VaultPickerDialog, LienPayantDialog } from './inbox/Pickers';
import OnboardingTour, { replayInboxTour, EVT_RELANCE_GUIDE } from '../../components/OnboardingTour';
import { getAllConvs, putConvs, getMsgs, putMsgs } from '../../lib/localdb';
import { DEMO } from '../../lib/demo';
import { scriptsPayes, scriptsDuBot } from '../../lib/scriptsPayes';
import { useAuth } from '../../context/AuthContext';

function ConvAvatar({ name, url, size, textSize = 'text-sm' }) {
  const [err, setErr] = useState(false);
  const initials = (name || '?').split(' ').filter(Boolean).slice(0, 2).map(x => x[0]).join('').toUpperCase();
  if (url && !err) {
    return <img src={url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} alt={name} onError={() => setErr(true)} className={`${size} rounded-full object-cover shrink-0`} />;
  }
  return <Avatar className={`${size} shrink-0`}><AvatarFallback className={`bg-gradient-to-br from-indigo-500/40 to-fuchsia-500/40 text-white ${textSize} font-semibold`}>{initials}</AvatarFallback></Avatar>;
}

// "Read, no reply": fan sent the last message AND we've already opened the chat (unread=0)
// → seen but unanswered. Module-scope (pure fn of `c`) so ConvRow's React.memo stays effective.
const isReadNoReply = (c) => c.unread === 0 && c.last_from === 'them' && !c.blocked && !c.is_group;

// Resynchro du fil : « même fil » = mêmes messages, dans le même ordre, avec les mêmes textes. Un lien réécrit par le
// bot change le texte sans changer l'id : comparer seulement la longueur et le dernier id gardait l'ancien lien affiché.
const memeFil = (a, b) => Array.isArray(a) && Array.isArray(b) && a.length === b.length
  && a.every((m, i) => m.id === b[i].id && (m.text || '') === (b[i].text || ''));

// DIAGNOSTIC LIEN PROFOND (04/09) : « Ouvrir la conv » n'ouvrait pas la bonne conv et les
// hypothèses successives se contredisaient → on trace les décisions en prod.
// Lecture : window.__dl dans la console. Marqueur de version : window.__DL_VERSION.
try { window.__DL_VERSION = 'deeplink-v3'; } catch (e) { /* SSR */ }
const dlLog = (etape, extra) => {
  try {
    const j = (window.__dl = window.__dl || []);
    j.push({ ms: Date.now() % 100000, etape, ...extra });
    if (j.length > 50) j.shift();
  } catch (e) { /* noop */ }
};

// 📢 Fil du canal Telegram du modèle (demande Marlon 02/09) : les chatteurs voient les
// derniers posts publiés sur le canal pour rebondir dessus avec les fans.
function ChannelFeedButton({ account }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = () => {
    setLoading(true);
    api.get('/channel/feed', { params: { account_id: account } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, 'Canal indisponible')))
      .finally(() => setLoading(false));
  };
  if (!account) return null;
  return (
    <>
      <button onClick={() => { setOpen(true); load(); }} title="Voir les derniers posts du canal Telegram du modèle"
        className="shrink-0 flex items-center gap-1.5 text-xs text-slate-400 hover:text-sky-300 hover:bg-white/5 rounded-lg px-2.5 py-1.5 border border-white/10">
        📢 <span className="hidden sm:inline">Canal</span>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0B0B0E] p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-bold text-white">📢 {data?.canal || 'Canal du modèle'}</div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white"><X size={16} /></button>
            </div>
            {loading ? <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> chargement…</div>
              : (data?.posts || []).length === 0 ? <div className="text-slate-400 text-sm">{data?.erreur || 'Aucun post sur le canal.'}</div>
                : (
                  <div className="space-y-2.5">
                    {(data.posts || []).map((p) => (
                      <div key={p.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                        <div className="text-[11px] text-slate-500 mb-1 flex items-center gap-2">
                          {p.quand ? new Date(p.quand).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                          {p.vues != null && <span>👁 {p.vues}</span>}
                        </div>
                        {/* PAS de loading="lazy" ici : dans cet overlay fixed, Chrome ne
                            déclenche jamais le lazy-load → images restées vides (bug 03/09). */}
                        {p.media && (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/channel/post-thumb?account_id=${account}&msg_id=${p.id}`}
                            alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            className="rounded-lg max-h-64 w-auto mb-2 border border-white/10" />
                        )}
                        <div className="text-[13px] text-slate-200 whitespace-pre-wrap">{p.texte || (p.media ? '' : '')}</div>
                      </div>
                    ))}
                  </div>
                )}
          </div>
        </div>
      )}
    </>
  );
}

// One conversation row, extracted + memoized. Switching the active chat used to re-render
// all ~60 rows (each a full Radix ContextMenu) → ~400ms main-thread block. With React.memo
// and stable props (handlers via useCallback, badges/allLists frozen), only the 2 affected
// rows (old + new active) re-render → conv switch is now near-instant, like Telegram.
const TAG_COLORS = { Baleine: '#37d39a', Baleines: '#37d39a', Spender: '#e8a13a', Spenders: '#e8a13a', Nouveau: '#3f7dff', VIP: '#3f7dff', PayPal: '#8b91a8', Crypto: '#8b91a8' };
const tagColor = (t) => TAG_COLORS[t] || '#8b91a8';

// Badge « NEW » : AUTOMATIQUE selon l'ancienneté du fan — il apparaît tant que la
// conversation a démarré il y a moins de NEW_FAN_DAYS jours (réglable). Plus besoin de
// le configurer à la main. (En prod : NEW_FAN_DAYS sera un réglage par agence.)
const NEW_FAN_DAYS = 3;
const isNewFan = (c) => {
  try { return !!c.created_at && (Date.now() - new Date(c.created_at).getTime()) < NEW_FAN_DAYS * 86400000; } catch (e) { return false; }
};

// Poignée de redimensionnement de la liste de conversations (Marlon : pouvoir « décaler » la
// largeur). Agit directement sur le DOM (pas de re-render) ; mémorise la largeur en localStorage.
function startConvResize(e) {
  e.preventDefault();
  const col = document.getElementById('conv-list-col');
  if (!col) return;
  const startX = e.clientX;
  const startW = col.getBoundingClientRect().width;
  const onMove = (ev) => {
    const w = Math.min(520, Math.max(260, startW + (ev.clientX - startX)));
    col.style.width = w + 'px';
  };
  const onUp = () => {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
    try { localStorage.setItem('chathub_conv_w', col.style.width); } catch (e2) { /* ignore */ }
  };
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
}
// Assistant IA — reprend l'outil de chatting de Marlon (hera-aichatting) DANS la messagerie.
// La conversation est déjà structurée → on reconstruit le transcript « Fan: / Modèle: » que
// l'IA attend (plus de copier-coller). Démo = suggestions locales ; prod = appel à son IA.
const AI_MODES = [['Relationnel', '❤️'], ['Sexualisation', '🔥'], ['Vente', '💸'], ['Relance', '🔁'], ['Négociation', '🤝']];
// Démo : chaque mode renvoie PLUSIEURS suggestions (en prod, l'IA renvoie la vraie
// séquence/les variantes). Sexualisation = séquence de messages à copier un par un. 15/09 : GFE fusionné dans Relationnel.
const AI_DEMO = {
  Relationnel: [
    "raconte-moi, c'était quoi le meilleur moment de ta journée ? 🥰",
    "t'es plutôt soirée tranquille ou tu sors ce week-end ?",
    "j'adore quand tu me parles de toi, dis-moi un truc que personne sait 🙈",
    "tu fais quoi dans la vie ? ça m'intrigue toi 👀",
    "avoue, je suis un peu ta préférée non ? 😌",
    "j'ai pensé à toi en me réveillant, c'est bête hein 🙈",
    "promets-moi un truc : tu m'écris ce soir avant de dormir 🥺",
  ],
  Sexualisation: [
    "avoue que je te donne des idées là 🙈",
    "j'ai pris une petite photo tout à l'heure… en pensant à toi 😏",
    "imagine, t'es à côté de moi là, tu ferais quoi en premier ?",
    "j'aime bien te faire patienter un peu… ça te rend fou hein 😈",
    "je te montre juste un aperçu, après faut être sage 🙊",
    "dis-moi ce qui te plaît vraiment, je note tout 😌",
    "t'es sûr que t'es prêt à voir la suite ? 🔥",
    "ok ok… dernière chance d'être sage avant que je t'envoie ça 😏",
  ],
  Vente: [
    "j'ai un truc rien que pour toi 🎁 tu vas adorer…",
    "je te montre un aperçu ? c'est du jamais vu ça 👀",
    "je le garde exprès pour mes préférés, t'en fais partie 😏",
  ],
  Relance: [
    "hé toi 🙈 tu m'as zappée ? reviens me parler…",
    "j'ai une surprise pour toi mais tu réponds plus 🥺",
    "ça fait un moment… tu me manques un peu là 💭",
  ],
  Négociation: [
    "je comprends, mais c'est déjà un prix juste pour ce que tu reçois 😌",
    "écoute, je peux pas brader, mais je te rajoute un petit extra rien que pour toi 🎁",
    "attends, t'as peut-être un souci avec ta carte ? dis-moi, on trouve une solution",
  ],
};
// Libellé FR (UI) -> clé de mode côté serveur (cf backend/ai_chat.py).
const AI_MODE_KEY = { 'Relationnel': 'rel', 'Sexualisation': 'sex', 'Vente': 'sell', 'Relance': 'cold', 'Négociation': 'nego' };
// Aplatit la réponse IA (ideas / sequence / s / approaches) en une liste de messages cliquables.
function flattenAi(data) {
  if (!data) return [];
  if (Array.isArray(data.ideas)) return data.ideas.map(i => i.text).filter(Boolean);
  if (Array.isArray(data.sequence)) return data.sequence.map(s => s.text).filter(Boolean);
  if (Array.isArray(data.s)) return data.s.map(x => x.t).filter(Boolean);
  if (Array.isArray(data.approaches)) return data.approaches.flatMap(a => (a.messages || [])).filter(Boolean);
  return [];
}
function AiAssistant({ messages, account, conversationId, onInsert }) {
  // 25/09 : l'assistant IA est une option Starter et plus. Sous Solo, le bouton
  // n'apparait pas (et /ai/suggest repond 402 de toute facon).
  const { hasFeature } = useAuth();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('Relationnel');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [copie, setCopie] = useState(false);
  // Copie tout le texte proposé d'un coup (repli si le navigateur refuse le presse-papiers).
  const copierTexte = (texte) => {
    try {
      if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(texte); return; }
    } catch (e) { /* repli ci-dessous */ }
    const z = document.createElement('textarea');
    z.value = texte; z.style.position = 'fixed'; z.style.opacity = '0';
    document.body.appendChild(z); z.select();
    try { document.execCommand('copy'); } catch (e) { /* rien de plus à faire */ }
    document.body.removeChild(z);
  };
  // Seulement les 20 DERNIERS messages : assez de contexte pour une bonne réponse, sans
  // envoyer toute la conv (évite les bugs de longueur + limite le coût de l'IA).
  const transcript = () => (messages || []).slice(-20).map(m => `${m.from_ === 'them' ? 'Fan' : (account?.name || 'Moi')}: ${m.text || '[média]'}`).join('\n');
  const gen = async (m) => {
    setLoading(true); setErr(''); setItems([]);
    if (DEMO) { setTimeout(() => { setItems(AI_DEMO[m] || []); setLoading(false); }, 400); return; }
    try {
      // PROD : l'appel part au SERVEUR ChatHub (clé Anthropic en env, jamais exposée).
      const { data } = await api.post('/ai/suggest', {
        mode: AI_MODE_KEY[m] || 'rel',
        model_label: account?.name || '',
        persona: account?.persona || account?.notes || '',
        transcript: transcript(),
        conversation_id: conversationId || '', // le serveur lit les notes de ce fan (bornées)
      });
      const flat = flattenAi(data?.result);
      setItems(flat.length ? flat : (AI_DEMO[m] || []));
    } catch (e) {
      setErr(e?.response?.status === 429 ? "Limite de générations atteinte pour aujourd'hui." : "Assistant IA indisponible — réessaie.");
    }
    setLoading(false);
  };
  if (!hasFeature('ai_assistant')) return null;
  return (
    <div className="relative shrink-0">
      <Button data-testid="ai-assistant-button" variant="outline" size="sm" onClick={() => { const n = !open; setOpen(n); if (n && !items.length) gen(mode); }}
        className="h-7 text-[11px] border-indigo-500/30 bg-indigo-500/10 text-indigo-200 hover:bg-indigo-500/20">✨ Assistant IA</Button>
      {open && (
        <div className="fixed bottom-[96px] left-1/2 -translate-x-1/2 w-[min(440px,92vw)] rounded-2xl border border-indigo-500/25 bg-[#0c0d14] shadow-2xl z-[60] p-3.5" data-testid="ai-panel">
          <div className="flex items-center justify-between mb-2.5">
            <span className="text-xs font-semibold text-white">✨ Suggestion IA — {account?.name || 'modèle'}</span>
            <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X size={14} /></button>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {AI_MODES.map(([m, e]) => (
              <button key={m} onClick={() => { setMode(m); gen(m); }} className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${mode === m ? 'bg-indigo-500/25 text-white border-indigo-500/50' : 'bg-white/[0.03] text-slate-400 border-white/10 hover:text-white'}`}>{e} {m}</button>
            ))}
          </div>
          {/* 19/09 (Marlon) : le texte se SÉLECTIONNE (il prend souvent 2-3 phrases d'un coup pour les copier).
              Le clic n'insère plus : c'est le ＋ à droite de chaque ligne, sinon on ne peut pas sélectionner tranquillement. */}
          <div data-testid="ai-suggestions" className="select-text cursor-text rounded-xl border border-white/10 bg-white/[0.02] p-2 min-h-[80px] max-h-[300px] overflow-y-auto space-y-1.5">
            {loading ? <span className="text-slate-500 text-[13px] block p-1">L'IA réfléchit…</span>
              : err ? <span className="text-rose-300 text-[13px] block p-1">{err}</span>
              : items.length === 0 ? <span className="text-slate-500 text-[13px] block p-1">Choisis un mode ci-dessus.</span>
              : items.map((t, i) => (
                <div key={i} data-testid={`ai-ligne-${i}`}
                  className="group flex items-start gap-1.5 rounded-lg px-2.5 py-2 bg-white/[0.02] border border-white/5 text-[13px] text-slate-200 leading-relaxed">
                  <span className="flex-1 select-text whitespace-pre-wrap">{t}</span>
                  <button onClick={() => { onInsert(t); setOpen(false); }} title="Insérer cette phrase dans la réponse"
                    data-testid={`ai-inserer-${i}`}
                    className="shrink-0 mt-px w-5 h-5 rounded text-[13px] leading-none text-slate-500 opacity-0 group-hover:opacity-100 hover:bg-indigo-500/20 hover:text-indigo-200 transition">＋</button>
                </div>
              ))}
          </div>
          <div className="flex items-center justify-between mt-3">
            <button onClick={() => gen(mode)} className="text-[11px] text-indigo-300 hover:text-indigo-200">↻ Régénérer</button>
            <div className="flex items-center gap-2.5">
              {items.length > 0 && (
                <button data-testid="ai-copier-tout" onClick={() => { copierTexte(items.join('\n')); setCopie(true); setTimeout(() => setCopie(false), 1500); }}
                  className="text-[11px] text-indigo-300 hover:text-indigo-200">{copie ? '✓ copié' : '⧉ Tout copier'}</button>
              )}
              <span className="text-[10px] text-slate-600">Sélectionne le texte, ou ＋ pour insérer</span>
            </div>
          </div>
          <p className="text-[10px] text-slate-600 mt-2">{DEMO ? "Démo — en prod, la conversation et les notes du fan partent à l'IA côté serveur (clé protégée)." : "L'IA lit la conversation et les notes du fan (jamais citées) — clé protégée côté serveur."}</p>
        </div>
      )}
    </div>
  );
}

// Bot d'accueil : « 🤖 avec le bot » tant que le bot l'accueille (Zone Bot, managers seulement). Les 3 jours qui suivent sa
// reprise : « 🤖 lien envoyé » seulement si le bot a envoyé son lien ; « 🤖 sans lien » s'il a été rendu après 2 h sans message du bot (avant : 12 h) ou
// parce que le bot est arrêté (il n'a jamais reçu le lien du bot) ; sinon « 🤖 repris du bot ».
const dateServeur = (iso) => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(String(iso)) ? iso : `${iso}Z`);
const ETIQUETTES_REPRISE = { lien_envoye: '🤖 lien envoyé', silence_bot: '🤖 pas eu le lien', delai: '🤖 pas eu le lien', bot_arrete: '🤖 pas eu le lien', plus_nouveau: '🤖 pas eu le lien' };
// 04/10 (B-379) : « sans réponse » = le dernier message de la conversation est celui du FAN. C'est le
// seul critère dont Marlon a besoin pour trier la Zone Bot d'un coup d'œil : soit le bot a répondu,
// soit le fan attend. `last_from` est ce que ChatHub utilise déjà partout pour cette question.
const sansReponse = (c) => c && c.last_from === 'them';

const etiquetteBot = (c) => {
  if (c.zone_bot) return sansReponse(c) ? '⏳ avec le bot — sans réponse' : '🤖 avec le bot';
  // 03/10 (B-346) : la Zone Bot réunit maintenant les DEUX bots. Sans étiquette distincte, Marlon ne
  // pouvait pas savoir lequel tient le fan — or ils n'ont ni le même rôle ni les mêmes relances.
  if (c.bot_hera) return sansReponse(c) ? '⏳ bot HERA — sans réponse' : '🤖 bot HERA';
  const r = c.bot_reprise;
  if (!r || !r.le) return null;
  const le = dateServeur(r.le);
  if (Number.isNaN(le.getTime()) || Date.now() - le.getTime() > 3 * 86400000) return null;
  return ETIQUETTES_REPRISE[r.raison] || '🤖 repris du bot';
};
// V5 : classement du fan par le bot après son lien, affiché 3 jours dans la liste et l'en-tête.
// 🟢 prêt à payer / 🟡 à négocier / 🟡 pas répondu au lien (2 h sans réponse) / 🔴 time waster (reste caché aux chatteurs).
const CLASSEMENT_JOURS = 3;
const COULEUR_CLASSEMENT = {
  pret: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/30',
  negocier: 'bg-amber-500/20 text-amber-200 border-amber-500/30',
  time_waster: 'bg-rose-500/20 text-rose-200 border-rose-500/30',
};
const texteClassement = (cl) => {
  if (!cl || !cl.classe) return null;
  if (cl.classe === 'pret') return '🟢 prêt à payer';
  if (cl.classe === 'negocier') return cl.sous_raison === 'sans_reponse' ? '🟡 pas répondu au lien' : '🟡 à négocier';
  if (cl.classe === 'time_waster') return '🔴 time waster';
  return null;
};
const classementBot = (c, toujours = false, venteLe = 0) => {
  const cl = c && c.bot_classement;
  const texte = texteClassement(cl);
  if (!texte || !cl.le) return null;
  const le = dateServeur(cl.le).getTime();
  if (Number.isNaN(le) || (!toujours && Date.now() - le > CLASSEMENT_JOURS * 86400000)) return null;
  // 15/09 : étiquette retirée à la main (le classement reste dans les notes) ; 🟡 qui a payé depuis → plus « à négocier »
  // (un fan n'entre en Zone Bot qu'à 0 dépensé : spent > 0 = payé après ; venteLe = dernière vente enregistrée, en ms).
  if (cl.retiree_le) return null;
  if (cl.classe === 'negocier' && (Number(c.spent || 0) > 0 || Number(venteLe || 0) > le)) return null;
  return { texte, classe: cl.classe, raison: cl.raison || '' };
};
// Tri par défaut : 🟢 puis 🟡 en haut ; tous les autres gardent leur ordre (tri stable).
const rangClassement = (c) => { const k = classementBot(c); return !k ? 2 : k.classe === 'pret' ? 0 : k.classe === 'negocier' ? 1 : 2; };
const estTimeWaster = (c) => ((c && c.bot_classement) || {}).classe === 'time_waster';

// V6 : pastille « 🤖 Lien envoyé » de la messagerie (tout le monde) : fans rendus par le bot après son lien, visibles dans
// la liste (30 jours), 🟢 puis 🟡 en tête. Jamais de 🔴 : le serveur ne les envoie plus, et un 🔴 rendu à la main n'y est pas.
const LIEN_ENVOYE = '__lien_envoye__';
const LIEN_ENVOYE_JOURS = 30;
const aRecuLienBot = (c) => {
  if (!c || c.zone_bot || estTimeWaster(c)) return false;
  const cl = c.bot_classement || {}, lien = c.bot_lien || {}, r = c.bot_reprise || {};
  if (!(cl.classe === 'pret' || cl.classe === 'negocier' || lien.le || r.raison === 'lien_envoye')) return false;
  const le = dateServeur(lien.le || cl.le || r.le).getTime();
  return !Number.isNaN(le) && Date.now() - le <= LIEN_ENVOYE_JOURS * 86400000;
};
const rangLienEnvoye = (c) => { const k = (c.bot_classement || {}).classe; return k === 'pret' ? 0 : k === 'negocier' ? 1 : 2; };

// E5 : fan hors Zone Bot à qui le bot (téléphone) a écrit il y a moins de 30 min → « 🤖 le bot lui parle », pour que le
// chatteur n'écrive pas en même temps. Renvoie les ms restantes (0 = pas d'étiquette).
const BOT_PARLE_MS = 30 * 60000;
const botParleReste = (c, maintenant) => {
  if (!c || c.zone_bot || !c.bot_parle_le) return 0;
  const t = dateServeur(c.bot_parle_le).getTime();
  return Number.isNaN(t) ? 0 : Math.max(0, Math.min(BOT_PARLE_MS, t + BOT_PARLE_MS - maintenant));
};

// 15/09 (Marlon) : clic sur l'étiquette de classement → « Retirer l'étiquette » (msg_manage_conv). Jamais pour un 🔴 caché.
function EtiquetteClassement({ c, k, testid, className, onRetirer }) {
  if (!onRetirer || k.classe === 'time_waster') {
    return <span data-testid={testid} title={k.raison} className={className}>{k.texte}</span>;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid={testid} title={k.raison} onClick={(e) => e.stopPropagation()}
          className={`${className} cursor-pointer hover:brightness-125`}>{k.texte}</button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()} className="bg-[#0B0B0E] border-white/10 text-slate-200">
        <DropdownMenuItem data-testid={`${testid}-retirer`} onSelect={() => onRetirer(c)} className="focus:bg-white/5 text-xs">Retirer l'étiquette</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const ConvRow = React.memo(function ConvRow({ c, isActive, botParle, classementToujours, badges, allLists, manualLists, toggleFanList, boxesEnabled, boxCount, canMoveBox, setActiveConv, prefetchConv, inputRef, markUnread, translateConversation, togglePin, setRenaming, setAddToList, moveToBox, blockConv, rendreAuxChatteurs, venteLe, retirerClassement }) {
  const etiquette = botParle ? '🤖 le bot lui parle' : etiquetteBot(c);
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div data-testid={`inbox-conv-${c.id}`} onClick={() => setActiveConv(c.id)} onMouseEnter={() => prefetchConv && prefetchConv(c.id)} className={`group cursor-pointer w-full text-left rounded-xl border px-3 py-2.5 flex items-center gap-2.5 transition-colors ${isActive ? 'bg-indigo-500/15 border-indigo-500/40' : c.pinned ? 'bg-white/[0.05] border-white/10 hover:border-white/20' : 'bg-white/[0.02] border-white/5 hover:border-white/[0.12]'}`}>
          <div className="relative shrink-0">
            <ConvAvatar name={c.name} url={c.profile_photo_url} size="w-9 h-9" textSize="text-xs" />
            {tgStatus(c).online && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-[#0c0d14]" aria-label="en ligne" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-1.5">
              <span className="text-[13px] font-semibold text-white truncate flex items-center gap-1.5 min-w-0">
                {c.pinned && <Pin size={11} className="text-indigo-400 fill-indigo-400 -rotate-45 shrink-0" aria-label="épinglée" />}
                <span className="truncate">{c.name}</span>
                {(() => { const k = classementBot(c, classementToujours, venteLe); if (k) return <EtiquetteClassement c={c} k={k} testid={`bot-classement-${c.id}`} className={`text-[9px] font-semibold rounded px-1.5 py-px shrink-0 border ${COULEUR_CLASSEMENT[k.classe]}`} onRetirer={retirerClassement} />; if (etiquette) return <span data-testid={`bot-etiquette-${c.id}`} className={`text-[9px] font-semibold rounded px-1.5 py-px shrink-0 border ${botParle ? 'bg-amber-500/20 text-amber-200 border-amber-500/30' : 'bg-violet-500/20 text-violet-200 border-violet-500/30'}`}>{etiquette}</span>; return isNewFan(c) ? <span className="text-[9px] font-bold rounded px-1.5 py-px shrink-0" style={{ background: '#37d39a', color: '#06281c' }}>NEW</span> : null; })()}
              </span>
              {c.unread > 0 && <span className="min-w-[18px] h-[18px] px-1.5 rounded-full text-white text-[10px] font-bold leading-none flex items-center justify-center shrink-0" style={{ background: '#3f7dff' }} data-testid={`unread-badge-${c.id}`}>{c.unread > 99 ? '99+' : c.unread}</span>}
            </div>
            {(() => {
              // Étiquettes MANUELLES : c.lists contient des IDs → on retrouve le nom via allLists.
              // (avant on lisait c.tags, qui n'existe pas → rien ne s'affichait à gauche.)
              // Uniquement les VRAIES listes manuelles (sans règle) : une liste AUTO épinglée
              // s'affiche via les badges `sm` ci-dessous → on évite le doublon.
              const manualTags = (c.lists || []).map(lid => (allLists || []).find(l => l.id === lid)).filter(l => l && !(l.rule_field && l.rule_op));
              // Smart lists AUTO, en masquant « Nouveaux fans » (déjà couvert par le badge NEW vert).
              const sm = (badges || []).filter(sl => !/nouve|new/i.test(sl.name || ''));
              if (!manualTags.length && !sm.length) return null;
              return (
                <div className="flex items-center gap-1 flex-wrap mt-1">
                  {manualTags.slice(0, 3).map(l => (
                    <span key={l.id} className="text-[9px] font-semibold px-1.5 py-px rounded-full" style={{ background: tagColor(l.name) + '22', color: tagColor(l.name) }}>{l.emoji ? l.emoji + ' ' : ''}{l.name}</span>
                  ))}
                  {sm.slice(0, 2).map(sl => (
                    <span key={`s-${sl.id}`} className={`text-[9px] px-1.5 py-px rounded-full border ${sl.color || 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30'}`}>{sl.emoji || '⚡'} {sl.name}</span>
                  ))}
                </div>
              );
            })()}
            <div className="text-[12px] truncate mt-1 flex items-center gap-1">
              {c.last_from === 'me' && <span className="text-indigo-300 font-medium shrink-0">Toi :</span>}
              <span className="text-slate-400 truncate">{c.last_msg}</span>
              <span className="text-slate-600 ml-auto shrink-0 flex items-center gap-0.5 text-[10px]">
                {c.last_from === 'me' && (c.last_seen ? <CheckCheck size={12} className="text-indigo-400" /> : <Check size={12} className="text-slate-500" />)}
                {timeAgo(c.last_activity)}
              </span>
            </div>
          </div>
          <button
            data-testid={`mark-unread-${c.id}`}
            onClick={(e) => markUnread(c, e)}
            title={isReadNoReply(c) ? 'Lu mais sans réponse — marquer comme non lu' : 'Marquer comme non lu'}
            className={`shrink-0 self-start transition-opacity ${
              isReadNoReply(c)
                ? 'opacity-100 text-amber-400 hover:text-amber-300'
                : 'opacity-0 group-hover:opacity-100 text-slate-500 hover:text-indigo-300'
            }`}
          >
            <Mail size={isReadNoReply(c) ? 14 : 13} />
          </button>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="bg-[#0B0B0E] border-white/10 text-slate-200 w-56">
        <ContextMenuItem data-testid={`ctx-reply-${c.id}`} onClick={() => { setActiveConv(c.id); setTimeout(() => inputRef.current?.focus(), 300); }} className="focus:bg-white/5"><CornerUpLeft size={13} className="mr-2" /> Répondre au fan</ContextMenuItem>
        <ContextMenuItem data-testid={`ctx-translate-${c.id}`} onClick={() => translateConversation(c)} className="focus:bg-white/5"><Languages size={13} className="mr-2 text-sky-400" /> Traduire les messages du fan</ContextMenuItem>
        <ContextMenuSeparator className="bg-white/5" />
        <ContextMenuItem data-testid={`ctx-pin-${c.id}`} onClick={() => togglePin(c)} className="focus:bg-white/5"><Pin size={13} className="mr-2" /> {c.pinned ? 'Désépingler' : 'Épingler en haut'}</ContextMenuItem>
        <ContextMenuItem onClick={(e) => markUnread(c, e)} className="focus:bg-white/5"><Mail size={13} className="mr-2" /> Marquer comme non lu</ContextMenuItem>
        <ContextMenuItem data-testid={`ctx-rename-${c.id}`} onClick={() => setRenaming(c)} className="focus:bg-white/5"><Pencil size={13} className="mr-2" /> Renommer...</ContextMenuItem>
        <ContextMenuSub>
          <ContextMenuSubTrigger className="focus:bg-white/5" data-testid={`ctx-lists-${c.id}`}><ListTree size={13} className="mr-2" /> Étiquettes</ContextMenuSubTrigger>
          <ContextMenuSubContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-h-72 overflow-y-auto">
            {(manualLists || []).length === 0 && <div className="px-2 py-1.5 text-[11px] text-slate-500">Aucune étiquette créée</div>}
            {(manualLists || []).map(l => {
              const on = (c.lists || []).includes(l.id);
              return (
                <ContextMenuItem key={l.id} data-testid={`ctx-list-${l.id}`} onSelect={(e) => { e.preventDefault(); toggleFanList(c, l.id); }} className="focus:bg-white/5 flex items-center gap-2 cursor-pointer">
                  <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-indigo-500 border-indigo-500' : 'border-white/25'}`}>{on && <Check size={10} className="text-white" />}</span>
                  <span className="truncate">{l.emoji || '●'} {l.name}</span>
                </ContextMenuItem>
              );
            })}
          </ContextMenuSubContent>
        </ContextMenuSub>
        {/* Fan de la Zone Bot : pas de « Gérer la Box » (il resterait caché aux chatteurs) ; on le rend. */}
        {canMoveBox && c.zone_bot && (
          <ContextMenuItem data-testid={`ctx-rendre-${c.id}`} onClick={() => rendreAuxChatteurs(c)} className="focus:bg-white/5"><Bot size={13} className="mr-2" /> Rendre aux chatteurs</ContextMenuItem>
        )}
        {boxesEnabled && canMoveBox && !c.zone_bot && (
          <ContextMenuSub>
            <ContextMenuSubTrigger className="focus:bg-white/5" data-testid={`ctx-box-${c.id}`}><FolderOpen size={13} className="mr-2" /> Gérer la Box</ContextMenuSubTrigger>
            <ContextMenuSubContent className="bg-[#0B0B0E] border-white/10 text-slate-200">
              {Array.from({ length: boxCount }, (_, i) => i + 1).map(b => (
                <ContextMenuItem key={b} data-testid={`ctx-move-box-${b}`} onClick={() => moveToBox(c, b)} className="focus:bg-white/5">
                  Box {b} {c.box === b && <Check size={12} className="ml-auto text-indigo-400" />}
                </ContextMenuItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>
        )}
        <ContextMenuSeparator className="bg-white/5" />
        <ContextMenuItem data-testid={`ctx-block-${c.id}`} onClick={() => blockConv(c)} className="focus:bg-rose-500/10 text-rose-400"><Ban size={13} className="mr-2" /> Bloquer</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
});

// 14/10 (lot 1 messagerie) : le champ annonce « … ou @pseudo » mais ne regardait que le nom (et plantait sur un
// nom vide). On cherche dans le nom ET dans le pseudo Telegram, « @ » facultatif.
function correspondRecherche(c, q) {
  const t = String(q || '').trim().toLowerCase();
  if (!t) return true;
  const sansArobase = t.replace(/^@/, '');
  return (c.name || '').toLowerCase().includes(t)
    || (!!sansArobase && (c.telegram_username || '').toLowerCase().replace(/^@/, '').includes(sansArobase));
}

export default function Inbox() {
  const { hasPerm } = useAuth();
  // E3 : « Bloqués » (interrupteur, ouverture d'un fan bloqué) pour qui voit les bloqués ; « Débloquer » pour qui peut bloquer.
  const peutVoirBloques = hasPerm('msg_view_blocked');
  const peutDebloquer = hasPerm('msg_block');
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const dragAccId = useRef(null);
  const [activeAccount, setActiveAccount] = useState('');
  // Compte RÉELLEMENT demandé en dernier : sert à ignorer une réponse /conversations
  // qui revient pour un modèle déjà quitté (sinon elle écrasait la liste du modèle courant
  // → on voyait/cliquait les convs du mauvais modèle pendant 1-2 s).
  const currentAccRef = useRef('');
  const [convs, setConvs] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [showInsight, setShowInsight] = useState(false); // panneau Notes/infos fan en plein écran (mobile/tablette)
  const [messages, setMessages] = useState([]);
  const convsCache = useRef({});  // accountId -> conversations (in-memory mirror of the IndexedDB local store)
  const msgsCache = useRef({});   // convId -> messages (in-memory mirror of the IndexedDB local store)
  const allSalesRef = useRef([]);  // mirror of allSales for instant per-conv filtering (no refetch)
  const draftsRef = useRef({});    // convId -> unsent draft text. CRITICAL: scopes the draft per fan so a
                                   // reply typed for one fan can NEVER be sent to another after switching.
  const draftValRef = useRef('');  // always-current draft value, to save it safely on conversation switch
  const [translations, setTranslations] = useState({}); // msgId -> translated text
  const [smartLists, setSmartLists] = useState([]);
  const [manualLists, setManualLists] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [scriptTags, setScriptTags] = useState([]);
  const [vault, setVault] = useState([]);
  const [convSales, setConvSales] = useState([]);
  const [allSales, setAllSales] = useState([]);
  const [box, setBox] = useState(0);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [blockedOnly, setBlockedOnly] = useState(false);   // voir les conversations bloquées
  const [blockedConvs, setBlockedConvs] = useState([]);
  // Zone Bot (bot d'accueil) : fans que le bot accueille encore — hors de la liste normale, managers seulement.
  const [zoneOnly, setZoneOnly] = useState(false);
  const [zoneConvs, setZoneConvs] = useState([]);
  const [zoneBot, setZoneBot] = useState(null);  // { bot, actif, nb_zone } pour le modèle affiché
  const [infoZone, setInfoZone] = useState(false);  // V6 : ⓘ du bandeau de la Zone Bot
  const zoneDemandee = useRef(searchParams.get('zone') === 'bot');  // lien « Ouvrir la Zone Bot » de la page Bot d'accueil
  const zoneConvsRef = useRef([]);   // miroir de zoneConvs pour les fonctions stables
  const zoneBotRef = useRef(false);  // le modèle affiché a la Zone Bot : sinon /zone-bot/compte n'est jamais relu
  const zoneMajT = useRef(null);     // regroupe les relectures du compteur Zone Bot
  const [oldestFirst, setOldestFirst] = useState(false);
  const [readNoReplyOnly, setReadNoReplyOnly] = useState(false);
  const [boxPanelOpen, setBoxPanelOpen] = useState(false);
  const [listFilter, setListFilter] = useState('');
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState('');
  const [pendingScript, setPendingScript] = useState(null);
  const [pendingVaults, setPendingVaults] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [scriptsOpen, setScriptsOpen] = useState(false);
  const [slashIdx, setSlashIdx] = useState(0);          // highlighted row in the "/" scripts menu
  const [slashDismissed, setSlashDismissed] = useState(null); // draft value Esc dismissed the menu for
  const [vaultOpen, setVaultOpen] = useState(false);
  const [lienMedias, setLienMedias] = useState(null);   // médias choisis pour un lien payant personnalisé
  const [renaming, setRenaming] = useState(null);
  const [emojiEdit, setEmojiEdit] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [reportMsg, setReportMsg] = useState(null);
  const [addToList, setAddToList] = useState(false);
  const [visibleCount, setVisibleCount] = useState(60);  // render only the first N conv rows (perf); grows on scroll
  const [visibleMsgCount, setVisibleMsgCount] = useState(40);  // render only the last N message bubbles (perf); grows on scroll-up
  const [inboxTourKey, setInboxTourKey] = useState(0);  // bump to replay the Messaging tour IN PLACE (no full page reload)
  // 29/09 : le bouton du Profil navigue ici puis demande la relance. Utile aussi quand on
  // est deja sur la messagerie (aucun remontage n'aurait lieu).
  useEffect(() => {
    const surDemande = (e) => { if (e.detail === 'inbox') setInboxTourKey(k => k + 1); };
    window.addEventListener(EVT_RELANCE_GUIDE, surDemande);
    return () => window.removeEventListener(EVT_RELANCE_GUIDE, surDemande);
  }, []);
  const msgScrollRef = useRef(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  // Each time the active chat changes, render only the last 40 bubbles again (a chat
  // you scrolled far up in shouldn't make the NEXT chat render hundreds of bubbles).
  useEffect(() => { setVisibleMsgCount(40); }, [activeConv]);
  // LIEN PROFOND ?account=&conv= (depuis Baleines, Ventes, un toast d'alerte…).
  // UNE SEULE source de vérité (`deepLink`) consommée à UN SEUL endroit — l'effet
  // [activeAccount]. ⚠️ Bug 04/09 : la consommation se faisait à DEUX endroits → cet
  // effet-ci ouvrait la conv, puis l'effet [activeAccount] rechargeait la liste SANS
  // présélection et la REFERMAIT aussitôt (setActiveConv(null)).
  const deepLink = useRef({ account: searchParams.get('account'), conv: searchParams.get('conv'), msg: searchParams.get('msg') });
  // &msg= (demandes des baleines) : message à amener à l'écran une fois la conversation ouverte.
  const msgCible = useRef(null);
  const chargeServeur = useRef('');  // dernière conversation dont l'historique serveur est arrivé

  useEffect(() => {
    const acc = searchParams.get('account');
    const cv = searchParams.get('conv');
    if (!acc && !cv) return;
    deepLink.current = { account: acc, conv: cv, msg: searchParams.get('msg') };
    dlLog('url', { acc: (acc || '').slice(0, 8), cv: (cv || '').slice(0, 8), actif: (activeAccount || '').slice(0, 8), nbComptes: accounts.length });
    if (acc && acc !== activeAccount && accounts.find(a => a.id === acc)) {
      setActiveAccount(acc);           // bascule de modèle → l'effet [activeAccount] consommera
    } else if (activeAccount) {
      consumeDeepLink(activeAccount);  // déjà sur le bon modèle → on ouvre tout de suite
    }
    // 1er rendu (comptes pas encore chargés) : l'effet [activeAccount] s'en charge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Hydrate the in-memory caches from the on-device IndexedDB store on mount, so the
  // conversations + chats you've ever opened render INSTANTLY — even after a reload or
  // days later (local-first, like Telegram reading from the device). Async; if it loses
  // a race with the first fetch, the fetch simply fills the gap. Failure = no cache.
  useEffect(() => {
    getAllConvs().then((all) => { convsCache.current = { ...all, ...convsCache.current }; }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/accounts').then(({ data }) => {
      const ready = data.filter(a => a.status === 'ready');
      setAccounts(ready);
      const fromUrl = deepLink.current.account;
      // Lien profond vers un modèle déconnecté : le dire, au lieu d'ouvrir en silence un AUTRE modèle.
      if (fromUrl && !ready.find(a => a.id === fromUrl)) {
        if (data.find(a => a.id === fromUrl)) toast.error("Ce modèle est déconnecté de Telegram : impossible d'ouvrir la conversation tant qu'il n'est pas reconnecté.");
        deepLink.current = { account: null, conv: null, msg: null };
        msgCible.current = null;
      }
      setActiveAccount(prev => prev || (fromUrl && ready.find(a => a.id === fromUrl) ? fromUrl : ready[0]?.id) || '');
      // Prefetch every model's conversation list in the background so switching models is
      // instant from the very first switch (local-first, like Telegram).
      ready.forEach(a => {
        api.get('/conversations', { params: { account_id: a.id } }).then(({ data: cs }) => {
          const sorted = cs.sort((x, y) => new Date(y.last_activity) - new Date(x.last_activity));
          convsCache.current[a.id] = sorted;
          putConvs(a.id, sorted);
        }).catch(() => {});
      });
    }).catch(e => toast.error(apiError(e, 'Impossible de charger les comptes')));
    api.get('/lists/smart').then(({ data }) => setSmartLists(data)).catch(() => {});
    api.get('/lists/manual').then(({ data }) => setManualLists(data)).catch(() => {});
    api.get('/sales').then(({ data }) => setAllSales(data)).catch(() => {});
    // Refresh sales on a slow cadence (instead of on every chat open) so per-conv sale
    // figures stay fresh without a heavy round-trip each time you switch conversation.
    const salesT = setInterval(() => { api.get('/sales').then(({ data }) => setAllSales(data)).catch(() => {}); }, 90000);
    return () => clearInterval(salesT);
  }, []);

  // Mirror allSales into a ref so chat-open can filter per-conv sales instantly (no refetch),
  // and keep the open conversation's sale figures in sync when the slow refresh lands.
  useEffect(() => {
    allSalesRef.current = allSales;
    if (activeConv) setConvSales(allSales.filter(s => s.conversation_id === activeConv));
  }, [allSales, activeConv]);

  // Prefetch a conversation's messages into the cache BEFORE it's opened, so the
  // first click renders instantly (no network wait). Fired on row hover and for the
  // top conversations after a list load. In-flight guard + cache check keep it cheap.
  const prefetchingRef = useRef(new Set());
  const prefetchConv = useCallback((cid) => {
    if (!cid || msgsCache.current[cid] || prefetchingRef.current.has(cid)) return;
    prefetchingRef.current.add(cid);
    // CRITICAL: peek=true loads the history WITHOUT marking the conversation read.
    // A prefetch (top conversations on load + on hover) must NEVER clear unread badges
    // — otherwise refreshing the page marks every prefetched chat as read.
    api.get(`/conversations/${cid}/messages`, { params: { peek: true } }).then(({ data }) => {
      msgsCache.current[cid] = data;
      putMsgs(cid, data);
    }).catch(() => {}).finally(() => prefetchingRef.current.delete(cid));
  }, []);

  // #47 : fan d'un lien profond absent de la liste normale → cherché dans la Zone Bot (fonction posée plus bas).
  const chercherEnZoneRef = useRef(() => {});
  // `lien` (lien profond) : { garder } = déjà sur ce modèle ; si le fan demandé est introuvable, rien ne se ferme.
  const loadConvs = useCallback((accId, preselect = null, lien = null) => {
    currentAccRef.current = accId;  // marque le modèle demandé en dernier
    dlLog('loadConvs', { compte: (accId || '').slice(0, 8), pre: (preselect || 'aucune').slice(0, 8) });
    const select = (sorted, frais) => {
      if (preselect === 'keep') return true;
      dlLog('select', { pre: (preselect || 'aucune').slice(0, 8), taille: sorted.length, trouve: !!(preselect && sorted.find(c => c.id === preselect)) });
      if (preselect && sorted.find(c => c.id === preselect)) {
        if (lien) {  // la conversation demandée est dans la liste normale
          setZoneOnly(false); setBlockedOnly(false);
          zoneConvsRef.current = []; setZoneConvs([]);
        }
        setActiveConv(preselect);
        return true;
      }
      if (lien && preselect) {
        // Pas dans la liste normale : on attend la réponse du serveur, puis on cherche dans la Zone Bot.
        if (frais) chercherEnZoneRef.current(accId, preselect, lien.garder);
        else if (!lien.garder) setActiveConv(null);
        return false;
      }
      setActiveConv(null);  // ne PAS ouvrir une conv au changement de modèle (évite de la marquer "vue" sans l'avoir lue)
      return false;
    };
    // Rendu INSTANTANÉ depuis le cache (bascule retour = zéro attente). Si pas encore en
    // cache, on VIDE la liste tout de suite → on ne voit/clique jamais les convs de l'ancien
    // modèle pendant le chargement. Puis on rafraîchit en arrière-plan.
    const cached = convsCache.current[accId];
    let picked = false;
    if (cached) { setConvs(cached); picked = select(cached, false); }
    else { setConvs([]); if (!lien || !lien.garder) setActiveConv(null); }
    api.get('/conversations', { params: { account_id: accId } }).then(({ data }) => {
      dlLog('reponse', { compte: (accId || '').slice(0, 8), courant: (currentAccRef.current || '').slice(0, 8), n: data.length, perimee: currentAccRef.current !== accId });
      if (currentAccRef.current !== accId) return;  // réponse périmée d'un modèle déjà quitté → ignorée
      const sorted = data.sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity));
      convsCache.current[accId] = sorted;
      putConvs(accId, sorted);
      setConvs(sorted);
      // Cache périmé + lien profond : la conv demandée n'y était pas → on re-sélectionne
      // sur les données fraîches (sinon « Ouvrir la conv » d'un fan récent ne faisait rien).
      if (!cached || (preselect && !picked)) select(sorted, true);
      // Warm the cache for the top conversations (staggered so the burst never
      // competes with the chat the user is opening right now).
      sorted.slice(0, 8).forEach((c, i) => setTimeout(() => prefetchConv(c.id), 400 + i * 250));
    }).catch(e => { if (!cached && currentAccRef.current === accId) toast.error(apiError(e, 'Impossible de charger les conversations')); });
  }, [prefetchConv]);

  // Ouvre la conversation d'un lien profond UNE seule fois, puis nettoie l'URL.
  const consumeDeepLink = useCallback((accId) => {
    const cv = deepLink.current.conv;
    dlLog('consume', { compte: (accId || '').slice(0, 8), cv: (cv || '').slice(0, 8) });
    if (!cv) return false;
    msgCible.current = deepLink.current.msg ? { conv: cv, id: deepLink.current.msg } : null;
    deepLink.current = { account: null, conv: null, msg: null };
    // Déjà sur ce modèle : la vue et la conversation ouvertes ne changent que si le fan demandé est trouvé (#47).
    const garder = accId === currentAccRef.current;
    if (!garder) { setZoneOnly(false); setBlockedOnly(false); }
    loadConvs(accId, cv, { garder });
    setSearchParams({}, { replace: true });
    return true;
  }, [loadConvs, setSearchParams]);

  // ⚠️ CAUSE RACINE du « Ouvrir la conv » qui n'ouvrait rien (tracée en prod le 04/09) :
  // cet effet dépend de `consumeDeepLink`, qui dépend de `setSearchParams` — dont
  // react-router CHANGE l'identité dès que l'URL change. Or `consumeDeepLink` nettoie
  // justement l'URL → l'effet se relançait 270 ms après avoir ouvert la conversation,
  // le lien profond était déjà consommé, et `loadConvs(compte, null)` la REFERMAIT.
  // Garde : on n'exécute le corps qu'une fois par vrai changement de compte.
  const loadedAccountRef = useRef('');
  useEffect(() => {
    if (!activeAccount || loadedAccountRef.current === activeAccount) return;
    loadedAccountRef.current = activeAccount;
    if (!consumeDeepLink(activeAccount)) loadConvs(activeAccount, null);
    // 14/10 (C-507) : passer vite de A à B pouvait laisser les scripts et le Vault de A affichés sur B (la
    // réponse de A arrivait après celle de B) ; « / » + Entrée prenait alors les médias de A. On vide tout de
    // suite, et une réponse qui n'est plus celle du modèle affiché est jetée (comme les autres lectures ici).
    setScripts([]); setScriptTags([]); setVault([]);
    const accScripts = activeAccount;
    api.get('/scripts', { params: { account_id: accScripts } }).then(({ data }) => { if (accScripts === currentAccRef.current) setScripts(data); }).catch(() => {});
    api.get('/script-tags', { params: { account_id: accScripts } }).then(({ data }) => { if (accScripts === currentAccRef.current) setScriptTags(data); }).catch(() => {});
    api.get('/vault', { params: { account_id: accScripts } }).then(({ data }) => { if (accScripts === currentAccRef.current) setVault(data); }).catch(() => {});
    setBox(0); setOnlineOnly(false); setUnreadOnly(false); setOldestFirst(false); setListFilter(''); setReadNoReplyOnly(false);
    setZoneOnly(false); setZoneConvs([]); setZoneBot(null); clearTimeout(zoneMajT.current);
    setBlockedConvs([]);  // les bloqués de l'ancien modèle ne restent pas affichés (ni cliquables) pendant le chargement
    if (hasPerm('msg_manage_boxes')) {
      const acc = activeAccount;
      api.get('/zone-bot/compte', { params: { account_id: acc } }).then(({ data }) => {
        if (acc !== currentAccRef.current || !data?.bot) return;
        setZoneBot(data);
        if (zoneDemandee.current) {
          zoneDemandee.current = false;
          setZoneOnly(true); setBlockedOnly(false); chargerZoneRef.current(acc);
          setSearchParams({}, { replace: true });
        }
      }).catch(() => {});  // section absente (bot pas en ligne) : pas d'onglet
    }
    setPendingScript(null); setPendingVaults([]); setReplyTo(null); setDraft('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccount, loadConvs, consumeDeepLink]);

  useEffect(() => {
    if (!activeConv) { setMessages([]); setConvSales([]); return undefined; }
    const cid = activeConv;
    // L'utilisateur est allé sur un autre fan avant que le message visé soit atteint : on oublie
    // la cible (sinon elle surlignerait un vieux message à la prochaine ouverture de ce fan).
    if (msgCible.current && msgCible.current.conv !== cid) msgCible.current = null;
    let cancelled = false;
    // Load THIS fan's own unsent draft (empty if none). Prevents a reply typed for a
    // previous fan from remaining in the composer and being sent to this one.
    setDraft(draftsRef.current[cid] || '');
    // Instant render: show this fan's cached history immediately (no blank/spinner),
    // then refresh in the background. Eliminates the lag when re-opening a chat.
    const cachedMsgs = msgsCache.current[cid];
    if (cachedMsgs) setMessages(cachedMsgs);
    // No in-memory copy yet? Pull this chat's history from the on-device store (async,
    // a few ms) so it still appears INSTANTLY — even on first open after a reload.
    else getMsgs(cid).then((local) => {
      if (local && !cancelled && activeConvRef.current === cid && !msgsCache.current[cid]) {
        msgsCache.current[cid] = local; setMessages(local);
      }
    }).catch(() => {});
    // Guard against a slow response landing AFTER the user switched conversation
    // — otherwise the previous fan's history overwrites the one on screen.
    api.get(`/conversations/${cid}/messages`).then(({ data }) => {
      msgsCache.current[cid] = data;
      putMsgs(cid, data);
      if (!cancelled && activeConvRef.current === cid) { chargeServeur.current = cid; setMessages(data); }
    }).catch(() => {
      if (!cancelled && !cachedMsgs && activeConvRef.current === cid) setMessages([]);
      if (msgCible.current?.conv === cid) {
        msgCible.current = null;
        toast.error("Impossible d'afficher le message pour l'instant : réessaie depuis la page Baleines.");
      }
    });
    // Sales: filter instantly from the already-loaded set (no heavy /sales refetch on every
    // chat open — that round-trip was one of the biggest per-open latency costs). A slow
    // background interval + sale WS events keep allSales fresh.
    setConvSales(allSalesRef.current.filter(s => s.conversation_id === cid));
    setPendingScript(null); setPendingVaults([]); setReplyTo(null); setTranslations({});
    // mark as read locally (liste normale, Zone Bot et « Bloqués »)
    setConvs(prev => prev.map(c => c.id === cid ? { ...c, unread: 0 } : c));
    setZoneConvs(prev => (prev.some(c => c.id === cid) ? prev.map(c => (c.id === cid ? { ...c, unread: 0 } : c)) : prev));
    setBlockedConvs(prev => (prev.some(c => c.id === cid) ? prev.map(c => (c.id === cid ? { ...c, unread: 0 } : c)) : prev));
    // On leaving this fan, stash whatever is currently typed under ITS id (not the
    // next fan's) so switching back restores it — and it never leaks to another fan.
    return () => { cancelled = true; draftsRef.current[cid] = draftValRef.current; };
  }, [activeConv]);

  // Keep draftValRef in sync with the live draft so the switch-cleanup above always
  // saves the LATEST typed text for the fan being left.
  useEffect(() => { draftValRef.current = draft; }, [draft]);

  // Jump to the latest message INSTANTLY (no smooth animation) so opening a chat shows
  // the last messages directly, without a visible scroll-down motion.
  // 13/09 : seulement quand on change de fan ou qu'un message arrive EN BAS. Une réaction, une modification ou
  // une suppression plus haut ne fait plus redescendre le fil pendant que le chatteur lit plus haut.
  // 15/09 (Marlon : « ça va seulement au niveau du lien, je dois scroller ») : le fil n'était descendu qu'UNE fois, après
  // l'affichage ; ensuite l'aperçu des liens Dropp et les photos (chargés après) l'agrandissaient → la vue restait plus haut.
  // Maintenant : descente AVANT l'affichage (useLayoutEffect), puis le fil reste COLLÉ EN BAS à chaque changement de
  // hauteur (aperçu, photo, vocal) tant que le chatteur n'a pas remonté lui-même (molette, doigt, barre, clavier).
  const collerEnBasRef = useRef(true);
  const gesteFilRef = useRef(0);
  const observateursFil = useRef(null);
  const descendreFil = useCallback(() => {
    const el = msgScrollRef.current;
    if (el && collerEnBasRef.current) el.scrollTop = el.scrollHeight;
  }, []);
  const brancherFil = useCallback((el) => {
    if (observateursFil.current) { observateursFil.current(); observateursFil.current = null; }
    msgScrollRef.current = el;
    if (!el) return;
    collerEnBasRef.current = true;
    el.scrollTop = el.scrollHeight;
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => descendreFil());
    ro.observe(el);
    Array.from(el.children).forEach(n => ro.observe(n));
    const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver((liste) => {
      liste.forEach((r) => {
        r.addedNodes.forEach((n) => { if (n.nodeType === 1) ro.observe(n); });
        r.removedNodes.forEach((n) => { if (n.nodeType === 1) ro.unobserve(n); });
      });
      descendreFil();
    });
    if (mo) mo.observe(el, { childList: true });
    observateursFil.current = () => { ro.disconnect(); if (mo) mo.disconnect(); };
  }, [descendreFil]);
  const noterGesteFil = useCallback(() => { gesteFilRef.current = Date.now(); }, []);
  const finDuFilRef = useRef({ conv: null, dernier: null });
  useLayoutEffect(() => {
    const dernier = messages.length ? messages[messages.length - 1].id : null;
    const avant = finDuFilRef.current;
    if (avant.conv !== activeConv || avant.dernier !== dernier) collerEnBasRef.current = true;
    finDuFilRef.current = { conv: activeConv, dernier };
    descendreFil();
  }, [messages, activeConv, descendreFil]);

  // ── REAL-TIME: WebSocket push from the backend (instant message sync). ──
  // The WebSocket is the PRIMARY sync channel. The polling effects below are a
  // safety net whose cadence ADAPTS: slow while the socket is healthy (light on
  // the single backend worker), fast only while it's down. On every (re)connect
  // we resync so no event is missed during a gap.
  const activeConvRef = useRef(null);
  const activeAccountRef = useRef('');
  const [wsUp, setWsUp] = useState(false);
  useEffect(() => { activeConvRef.current = activeConv; }, [activeConv]);
  const zoneOnlyRef = useRef(false);
  useEffect(() => { zoneOnlyRef.current = zoneOnly; }, [zoneOnly]);
  const blockedOnlyRef = useRef(false);
  useEffect(() => { blockedOnlyRef.current = blockedOnly; }, [blockedOnly]);
  useEffect(() => { zoneConvsRef.current = zoneConvs; }, [zoneConvs]);
  useEffect(() => { zoneBotRef.current = !!zoneBot?.bot; }, [zoneBot]);
  useEffect(() => { activeAccountRef.current = activeAccount; }, [activeAccount]);

  // 20/09 (Marlon : « quand j'essaie de supprimer des messages, il y a un léger bug des fois »).
  // Le bouton Supprimer ne retirait le message que de l'AFFICHAGE : il comptait sur l'évènement
  // « message_deleted » du serveur pour purger le cache local (mémoire + IndexedDB). Si cet
  // évènement se perdait — reconnexion du flux, onglet en arrière-plan, course avec l'envoi —
  // le message revenait au rechargement ou en rouvrant la conversation. D'où le « des fois ».
  // On purge donc les trois endroits tout de suite ; l'évènement reste, il devient juste redondant.
  const supprimerDuFil = useCallback((id) => {
    setMessages((prev) => prev.filter((x) => x.id !== id));
    const cid = activeConvRef.current;
    if (!cid) return;
    const cm = msgsCache.current[cid];
    if (!cm) return;
    const filtre = cm.filter((x) => x.id !== id);
    msgsCache.current[cid] = filtre;
    // La chaîne ci-dessous sert aussi de repère : un nom de fonction est minifié dans le bundle,
    // pas un littéral — c'est ce qui permet de vérifier que le correctif est bien en ligne.
    try { putMsgs(cid, filtre); } catch (_) { console.warn('[suppression] cache local non purge'); }
  }, []);

  // Recharge la liste du modèle affiché. Une réponse qui arrive après un changement de modèle est ignorée
  // (sinon la liste d'un modèle s'affichait sous un autre).
  const rechargerListe = useCallback((acc) => {
    if (!acc || acc !== activeAccountRef.current) return;
    api.get('/conversations', { params: { account_id: acc } }).then(({ data }) => {
      if (acc !== activeAccountRef.current || acc !== currentAccRef.current) return;
      const sorted = data.sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity));
      setConvs(sorted.map(c => (c.id === activeConvRef.current ? { ...c, unread: 0 } : c)));
    }).catch(() => {});
  }, []);

  // Fil venu du serveur (sondage, resynchro) : on garde l'affichage s'il n'a pas changé, sinon on prend celui du serveur,
  // caches locaux compris (sauf si le cache a une bulle en cours d'envoi ou en échec).
  const appliquerFilServeur = useCallback((cid, data) => {
    if (!Array.isArray(data)) return;
    const cm = msgsCache.current[cid];
    if (cm && !memeFil(cm, data) && !cm.some(m => m._pending || m._failed)) { msgsCache.current[cid] = data; putMsgs(cid, data); }
    if (activeConvRef.current !== cid) return;
    setMessages(prev => (memeFil(prev, data) ? prev : data));
  }, []);

  // Zone Bot : relit le compteur (ou la liste, si elle est ouverte), sans toast. Rien si le modèle affiché n'a pas le bot.
  const rafraichirZone = useCallback((acc) => {
    if (!acc || acc !== currentAccRef.current || !zoneBotRef.current) return;
    if (zoneOnlyRef.current) { chargerZoneRef.current(acc, true); return; }
    api.get('/zone-bot/compte', { params: { account_id: acc } }).then(({ data }) => {
      if (acc !== currentAccRef.current || zoneOnlyRef.current) return;
      setZoneBot(data?.bot ? data : null);
    }).catch(() => {});
  }, []);
  // Plusieurs évènements d'affilée → une seule relecture.
  const planifierZone = useCallback((acc) => {
    clearTimeout(zoneMajT.current);
    zoneMajT.current = setTimeout(() => rafraichirZone(acc), 1000);
  }, [rafraichirZone]);

  // Catch-up refetch used on WS (re)connect and as the polling body.
  const resyncRef = useRef(() => {});
  resyncRef.current = () => {
    const acc = activeAccountRef.current;
    const cid = activeConvRef.current;
    if (acc) {
      rechargerListe(acc);
      rafraichirZone(acc);
    }
    if (cid) {
      // 15/10 (lot 2 messagerie) : onglet CACHÉ → lecture « peek » (personne ne regarde : le fan ne doit pas voir « lu »).
      // Au retour sur l'onglet, le rattrapage (onglet visible) marque lu normalement.
      const params = document.hidden ? { peek: true } : undefined;
      api.get(`/conversations/${cid}/messages`, { params }).then(({ data }) => appliquerFilServeur(cid, data)).catch(() => {});
    }
    api.get('/accounts').then(({ data }) => setAccounts(data.filter(a => a.status === 'ready'))).catch(() => {});
  };

  useEffect(() => {
    let ws = null, closed = false, retryT = null, attempt = 0;
    const scheduleRetry = () => {
      if (closed) return;
      attempt = Math.min(attempt + 1, 5);
      retryT = setTimeout(connect, Math.min(2000 * attempt, 15000)); // gentle backoff, capped 15s
    };
    function connect() {
      try {
        ws = new WebSocket(`${process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws')}/api/ws`);
      } catch { scheduleRetry(); return; }
      ws.onopen = () => { attempt = 0; setWsUp(true); resyncRef.current(); }; // catch up on (re)connect
      ws.onmessage = (e) => {
        let ev; try { ev = JSON.parse(e.data); } catch { return; }
        if (ev.type === 'message' && !(ev.message && ev.message.id)) {
          // 14/10 (lot 1 messagerie) : les envois du bot par ChatHub publient {"type":"message"} SANS le message.
          // On lisait `ev.message.id` → TypeError : le reste du traitement sautait, et un fil vide pouvait
          // planter l'écran. On relit simplement ce qui est concerné, sans rien deviner.
          if (ev.conversation_id && ev.conversation_id === activeConvRef.current) resyncRef.current();
          else if (activeAccountRef.current) rechargerListe(activeAccountRef.current);
        } else if (ev.type === 'message') {
          // Keep the on-device store current: append the new message to this chat's
          // cached history so re-opening it shows the latest instantly (no re-fetch).
          const cm = msgsCache.current[ev.conversation_id];
          if (cm && !cm.some(x => x.id === ev.message.id)) {
            const merged = [...cm, ev.message];
            msgsCache.current[ev.conversation_id] = merged;
            putMsgs(ev.conversation_id, merged);
          }
          if (ev.conversation_id === activeConvRef.current) {
            setMessages(prev => (prev.some(x => x.id === ev.message.id) ? prev : [...prev, ev.message]));
          }
          // E5 : le bot vient d'écrire à ce fan depuis le téléphone → « 🤖 le bot lui parle » tout de suite.
          if (ev.bot_parle_le) majLocale(ev.conversation_id, { bot_parle_le: ev.bot_parle_le });
          if (ev.account_id === activeAccountRef.current) rechargerListe(ev.account_id);
          api.get('/accounts').then(({ data }) => setAccounts(data.filter(a => a.status === 'ready'))).catch(() => {});
          // Bot d'accueil : un fan parle au bot → compteur (et liste ouverte) de la Zone Bot à jour, même Zone Bot fermée.
          if (ev.zone_bot && ev.account_id === activeAccountRef.current) planifierZone(ev.account_id);
        } else if (ev.type === 'conversation_update') {
          // E5 : évènement qui ne porte que l'étiquette « 🤖 le bot lui parle » (pas de clé zone_bot) → posée sur place, sans
          // relire la liste ni le compteur Zone Bot (le message du bot a déjà rafraîchi la liste).
          if (ev.bot_parle_le) {
            majLocale(ev.conversation_id, { bot_parle_le: ev.bot_parle_le });
            if (!Object.prototype.hasOwnProperty.call(ev, 'zone_bot')) return;
          }
          // 15/09 : étiquette de classement retirée par quelqu'un de l'équipe → retirée sur place chez tout le monde.
          if (ev.bot_classement) {
            majLocale(ev.conversation_id, { bot_classement: ev.bot_classement });
            if (!Object.prototype.hasOwnProperty.call(ev, 'zone_bot')) return;
          }
          // Bot d'accueil : un fan sort de la Zone Bot (il arrive dans une box) → liste et Zone Bot à jour.
          if (ev.account_id === activeAccountRef.current) {
            rechargerListe(ev.account_id);
            planifierZone(ev.account_id);
          }
        } else if (ev.type === 'message_edited' && ev.message_id && typeof ev.text === 'string') {
          // Bot d'accueil : lien du bot réécrit au nom du fan → même message, nouveau texte (écran + caches locaux).
          const cid = ev.conversation_id;
          const upd = (x) => (x.id === ev.message_id ? { ...x, text: ev.text } : x);
          const corriger = (liste) => {
            if (!liste || !liste.some(x => x.id === ev.message_id)) return;
            const m = liste.map(upd);
            if (msgsCache.current[cid]) msgsCache.current[cid] = m;
            putMsgs(cid, m);
          };
          if (msgsCache.current[cid]) corriger(msgsCache.current[cid]);
          else getMsgs(cid).then((local) => corriger(msgsCache.current[cid] || local)).catch(() => {});
          if (cid === activeConvRef.current) {
            setMessages(prev => (prev.some(x => x.id === ev.message_id && x.text !== ev.text) ? prev.map(upd) : prev));
          }
          if (ev.account_id === activeAccountRef.current) {
            rechargerListe(ev.account_id);  // aperçu du dernier message dans la liste
            if (ev.zone_bot) planifierZone(ev.account_id);
          }
        } else if (ev.type === 'conversation_read') {
          // The model read this dialog on her phone/official app — sync badges
          setConvs(prev => prev.map(c => (c.id === ev.conversation_id ? { ...c, unread: ev.unread } : c)));
          api.get('/accounts').then(({ data }) => setAccounts(data.filter(a => a.status === 'ready'))).catch(() => {});
        } else if (ev.type === 'message_deleted') {
          const cm = msgsCache.current[ev.conversation_id];
          if (cm) { const f = cm.filter(x => x.id !== ev.message_id); msgsCache.current[ev.conversation_id] = f; putMsgs(ev.conversation_id, f); }
          // Seulement le fil ouvert, et seulement s'il contient ce message (sinon on garde la même liste).
          if (ev.conversation_id === activeConvRef.current) {
            setMessages(prev => (prev.some(x => x.id === ev.message_id) ? prev.filter(x => x.id !== ev.message_id) : prev));
          }
        } else if (ev.type === 'reactions') {
          const upd = (x) => (x.id === ev.message_id
            ? { ...x, reactions: ev.reactions, ...(ev.my_reaction !== undefined ? { my_reaction: ev.my_reaction } : {}) }
            : x);
          const cm = msgsCache.current[ev.conversation_id];
          if (cm) { const m = cm.map(upd); msgsCache.current[ev.conversation_id] = m; putMsgs(ev.conversation_id, m); }
          if (ev.conversation_id === activeConvRef.current) {
            setMessages(prev => (prev.some(x => x.id === ev.message_id) ? prev.map(upd) : prev));
          }
        }
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
      ws.onclose = () => { setWsUp(false); scheduleRetry(); };
    }
    connect();
    return () => { closed = true; clearTimeout(retryT); clearTimeout(zoneMajT.current); setWsUp(false); try { ws && ws.close(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the app regains focus, the browser may have throttled timers and suspended the
  // WebSocket while it was in the background — leaving the open thread frozen on old
  // messages (the "messages pas à jour quand je reviens" bug). Resync IMMEDIATELY on
  // refocus so the latest messages + conversation list snap up to date at once.
  useEffect(() => {
    // 15/10 (lot 2) : `focus` et `visibilitychange` arrivent ensemble au retour → un seul rattrapage (1,5 s d'écart mini).
    // Le rattrapage à la reconnexion du temps réel (ws.onopen) n'est PAS concerné : il passe toujours.
    let dernier = 0;
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      const maintenant = Date.now();
      if (maintenant - dernier < 1500) return;
      dernier = maintenant;
      resyncRef.current();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  // Adaptive polling — active conversation messages. Slow when realtime is up.
  useEffect(() => {
    if (!activeConv) return undefined;
    const cid = activeConv;
    const t = setInterval(() => {
      if (document.hidden) return;  // 15/10 (lot 2) : onglet caché → pas de relecture (ni « lu » chez le fan) ; rattrapage au retour
      api.get(`/conversations/${cid}/messages`).then(({ data }) => appliquerFilServeur(cid, data)).catch(() => {});
    }, wsUp ? 20000 : 4000);
    return () => clearInterval(t);
  }, [activeConv, wsUp, appliquerFilServeur]);

  // Adaptive polling — conversation list + account unread badges.
  useEffect(() => {
    if (!activeAccount) return;
    const t = setInterval(() => {
      if (document.hidden) return;  // 15/10 (lot 2) : onglet caché → pas de relecture ; le rattrapage au retour remet tout à jour
      api.get('/conversations', { params: { account_id: activeAccount } }).then(({ data }) => {
        if (activeAccountRef.current !== activeAccount) return; // user switched account mid-flight — don't apply stale data
        const sorted = data.sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity));
        setConvs(sorted.map(c => (c.id === activeConvRef.current ? { ...c, unread: 0 } : c)));
      }).catch(() => {});
      api.get('/accounts').then(({ data }) => setAccounts(data.filter(a => a.status === 'ready'))).catch(() => {});
      rafraichirZone(activeAccount);  // compteur Zone Bot (et liste ouverte), même sans temps réel
    }, wsUp ? 30000 : 6000);
    return () => clearInterval(t);
  }, [activeAccount, activeConv, wsUp, rafraichirZone]);

  // (DropFans new-sale popup now lives in DashboardLayout — app-wide.)

  const account = accounts.find(a => a.id === activeAccount);
  // Fan de « Bloqués » (hors de convs) : il s'ouvre pour qui voit les bloqués, et seulement s'il est du modèle affiché.
  const conv = convs.find(c => c.id === activeConv)
    || (zoneOnly ? zoneConvs.find(c => c.id === activeConv)
      : blockedOnly && peutVoirBloques ? blockedConvs.find(c => c.id === activeConv && c.account_id === activeAccount) : undefined);

  // BOUTON « DERNIER LIEN » (04/09, demande Marlon) : ramène au dernier lien de paiement
  // envoyé dans cette conversation et surligne le message ~3 s.
  const dernierLien = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].has_dropfans_link) return { m: messages[i], index: i };
    }
    return null;
  }, [messages]);

  // ⚠️ Le surlignage doit être un ÉTAT React : posé en classe DOM à la main, il était
  // effacé au premier re-rendu du fil (le rafraîchissement des messages arrive juste
  // après l'ouverture de la conversation) — vérifié le 05/09.
  const [msgSurligne, setMsgSurligne] = useState(null);

  const allerAuDernierLien = useCallback(() => {
    if (!dernierLien) return;
    collerEnBasRef.current = false;  // 15/09 : le fil ne se recolle pas en bas pendant qu'on va au lien
    // Le fil ne rend que les N derniers messages : on élargit d'abord si le lien est plus haut.
    const besoin = messages.length - dernierLien.index + 5;
    if (besoin > visibleMsgCount) setVisibleMsgCount(besoin);
    const id = dernierLien.m.id;
    const aller = () => {
      const el = document.getElementById(`msg-${id}`);
      if (!el) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    };
    // Après un élargissement, le message n'est pas encore monté au 1er essai.
    requestAnimationFrame(() => { if (!aller()) setTimeout(aller, 150); });
    setMsgSurligne(id);
    setTimeout(() => setMsgSurligne((cur) => (cur === id ? null : cur)), 3000);
  }, [dernierLien, messages.length, visibleMsgCount]);

  // LIEN VERS UN MESSAGE PRÉCIS (&msg=, depuis les demandes des baleines) : une fois
  // l'historique SERVEUR arrivé (sinon le rafraîchissement suivant ramènerait en bas du fil),
  // on amène le message à l'écran et on le surligne ~4 s.
  useEffect(() => {
    const cible = msgCible.current;
    if (!cible || cible.conv !== activeConv || chargeServeur.current !== activeConv || !messages.length) return;
    msgCible.current = null;
    collerEnBasRef.current = false;  // 15/09 : on va au message visé, pas en bas
    const index = messages.findIndex(m => m.id === cible.id);
    if (index < 0) {
      toast.info("Ce message est au-delà des 500 derniers messages chargés ici : la messagerie ne peut pas l'afficher. Il reste lisible depuis la page Baleines (« Voir le message »).");
      return;
    }
    const besoin = messages.length - index + 5;
    setVisibleMsgCount(v => Math.max(v, besoin));
    const aller = () => {
      const el = document.getElementById(`msg-${cible.id}`);
      if (!el) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    };
    requestAnimationFrame(() => { if (!aller()) setTimeout(aller, 250); });
    setMsgSurligne(cible.id);
    setTimeout(() => setMsgSurligne((cur) => (cur === cible.id ? null : cur)), 4000);
  }, [messages, activeConv]);
  const boxCount = account?.boxes || 3;
  const allLists = useMemo(() => [...smartLists, ...manualLists], [smartLists, manualLists]);
  // Étiquettes que le chatteur peut COCHER au clic droit : les manuelles + les listes
  // AUTO basées sur la dépense (Baleines, VIP, Top spenders…). Permet d'ajouter à la main
  // un vrai gros payeur que la règle n'a pas attrapé (anciens paiements non comptés) →
  // plus besoin d'une 2ᵉ liste en double.
  const taggableLists = useMemo(
    () => [...manualLists, ...smartLists.filter(s => s.rule_field === 'total_spent')],
    [manualLists, smartLists]);

  // Stabilized with useCallback so ConvRow's React.memo isn't defeated (a new function
  // identity every render would force all rows to re-render on each state change).
  const patchConv = useCallback(async (cid, patch) => {
    try {
      const { data } = await api.patch(`/conversations/${cid}`, patch);
      // Réponse = la conversation à jour ; si ce n'en est pas une, on applique le changement envoyé.
      const maj = (c) => (data && data.id === cid ? data : { ...c, ...patch });
      setConvs(prev => prev.map(c => c.id === cid ? maj(c) : c)
        .filter(c => !c.blocked)
        .sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity)));
      // Fan ouvert depuis « Bloqués » : sa ligne suit (nom, notes, étiquettes, épingle) ; débloqué → il quitte cette liste.
      setBlockedConvs(prev => (prev.some(c => c.id === cid) ? prev.map(c => (c.id === cid ? maj(c) : c)).filter(c => c.blocked) : prev));
      // Fan affiché dans la Zone Bot : mis à jour ; bloqué ou sorti de la zone → il quitte la liste et le compteur.
      const enZone = zoneConvsRef.current.find(c => c.id === cid);
      if (enZone) {
        const apres = maj(enZone);
        if (!apres.zone_bot || apres.blocked) {
          zoneConvsRef.current = zoneConvsRef.current.filter(c => c.id !== cid);
          setZoneConvs(prev => prev.filter(c => c.id !== cid));
          if (!estTimeWaster(enZone)) setZoneBot(z => (z ? { ...z, nb_zone: Math.max(0, (z.nb_zone || 0) - 1) } : z));
        } else {
          setZoneConvs(prev => prev.map(c => (c.id === cid ? maj(c) : c)));
        }
      }
      return data;
    } catch (e) { toast.error(apiError(e, 'Échec de la mise à jour')); return null; }
  }, []);

  // Mise à jour optimiste d'un fan, dans la liste normale, la Zone Bot et « Bloqués » (le fan ouvert vient de l'une d'elles).
  const majLocale = useCallback((cid, champs) => {
    setConvs(prev => prev.map(x => (x.id === cid ? { ...x, ...champs } : x)));
    setZoneConvs(prev => (prev.some(x => x.id === cid) ? prev.map(x => (x.id === cid ? { ...x, ...champs } : x)) : prev));
    setBlockedConvs(prev => (prev.some(x => x.id === cid) ? prev.map(x => (x.id === cid ? { ...x, ...champs } : x)) : prev));
  }, []);

  // 15/09 (Marlon) : « Retirer l'étiquette » 🟢/🟡 — retirée tout de suite à l'écran, remise si le serveur refuse.
  const retirerClassement = useCallback(async (c) => {
    const avant = c.bot_classement;
    if (!avant) return;
    majLocale(c.id, { bot_classement: { ...avant, retiree_le: new Date().toISOString() } });
    try {
      const { data } = await api.post(`/conversations/${c.id}/bot-classement/retirer`);
      if (data && data.bot_classement) majLocale(c.id, { bot_classement: data.bot_classement });
      toast.success('Étiquette retirée');
    } catch (e) {
      majLocale(c.id, { bot_classement: avant });
      toast.error(apiError(e, "Impossible de retirer l'étiquette"));
    }
  }, [majLocale]);
  const peutRetirerClassement = hasPerm('msg_manage_conv');
  // Dernière vente (non annulée) de chaque fan, en ms : un 🟡 qui a payé depuis son classement n'affiche plus « à négocier ».
  const derniereVenteParConv = useMemo(() => {
    const m = new Map();
    (allSales || []).forEach((s) => {
      if (!s || !s.conversation_id || ['rejected', 'withdrawn', 'refunded', 'duplicate'].includes(s.status)) return;
      const t = dateServeur(s.date || s.created_at).getTime();
      if (!Number.isNaN(t) && t > (m.get(s.conversation_id) || 0)) m.set(s.conversation_id, t);
    });
    return m;
  }, [allSales]);

  const togglePin = useCallback(async (c) => {
    const voulu = !c.pinned;
    majLocale(c.id, { pinned: voulu });  // tout de suite à l'écran : on peut enchaîner sans attendre le serveur
    toast.success(voulu ? 'Conversation épinglée' : 'Épingle retirée', { id: 'epingle', duration: 800 });
    const r = await patchConv(c.id, { pinned: voulu });
    if (!r) majLocale(c.id, { pinned: !voulu });  // refusé : on remet comme avant (le message d'erreur est déjà affiché)
  }, [patchConv, majLocale]);

  // Ajoute/retire une étiquette sur un fan DIRECTEMENT depuis le clic droit (sans ouvrir
  // la conversation). Optimiste : l'UI bouge tout de suite, puis on sauvegarde.
  const toggleFanList = useCallback((c, listId) => {
    const cur = c.lists || [];
    const next = cur.includes(listId) ? cur.filter(x => x !== listId) : [...cur, listId];
    majLocale(c.id, { lists: next });
    patchConv(c.id, { lists: next });
  }, [patchConv, majLocale]);

  // Les bloquées vivent hors de la liste normale (l'API les exclut par défaut) :
  // on va les chercher séparément quand le filtre est activé.
  const chargerBloquees = useCallback(async (accId) => {
    if (!accId) return;
    try {
      const { data } = await api.get('/conversations', { params: { account_id: accId, blocked: true } });
      // Réponse d'un modèle déjà quitté (onglet changé pendant le chargement) : ignorée.
      if (accId !== currentAccRef.current || accId !== activeAccountRef.current) return;
      setBlockedConvs(data || []);
    } catch (e) { if (accId === currentAccRef.current) toast.error(apiError(e, 'Échec du chargement des bloqués')); }
  }, []);

  const chargerZone = useCallback(async (accId, silencieux = false) => {
    if (!accId) return;
    try {
      const { data } = await api.get('/conversations', { params: { account_id: accId, zone_bot: true } });
      if (accId !== currentAccRef.current) return;
      const tries = (data || []).sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity));
      setZoneConvs(tries);
      setZoneBot(z => (z ? { ...z, nb_zone: tries.filter(c => !estTimeWaster(c)).length } : z));
    } catch (e) { if (!silencieux) toast.error(apiError(e, 'Échec du chargement de la Zone Bot')); }
  }, []);
  const chargerZoneRef = useRef(chargerZone);
  chargerZoneRef.current = chargerZone;

  // #47 : « Ouvrir le chat » (notification, lien profond) vers un fan encore en Zone Bot : un manager l'ouvre dans la Zone Bot.
  // Introuvable : rien ne se ferme sur le modèle affiché.
  chercherEnZoneRef.current = (accId, cv, garder) => {
    const introuvable = () => {
      if (accId !== currentAccRef.current) return;
      if (!garder) setActiveConv(null);
      toast.info('Conversation introuvable');
    };
    if (!hasPerm('msg_manage_boxes')) { introuvable(); return; }
    api.get('/conversations', { params: { account_id: accId, zone_bot: true } }).then(({ data }) => {
      if (accId !== currentAccRef.current) return;
      const tries = (data || []).sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity));
      if (!tries.some(c => c.id === cv)) { introuvable(); return; }
      zoneConvsRef.current = tries;
      setZoneConvs(tries);
      setZoneBot(z => (z ? { ...z, nb_zone: tries.filter(c => !estTimeWaster(c)).length } : z));
      setBlockedOnly(false); setZoneOnly(true);
      setActiveConv(cv);
    }).catch(introuvable);
  };

  const rendreAuxChatteurs = useCallback(async (c) => {
    try {
      const { data } = await api.post(`/zone-bot/${c.id}/rendre`);
      const r = data?.reprise || {};
      if (zoneConvsRef.current.some(x => x.id === c.id)) {
        zoneConvsRef.current = zoneConvsRef.current.filter(x => x.id !== c.id);
        setZoneConvs(l => l.filter(x => x.id !== c.id));
        if (!r.bloque && !estTimeWaster(c)) setZoneBot(z => (z ? { ...z, nb_zone: Math.max(0, (z.nb_zone || 1) - 1) } : z));
      }
      if (r.bloque) {
        // Fan bloqué (D5) : il sort de la Zone Bot sans chatteur ni notification, et reste dans « Bloqués ».
        setBlockedConvs(l => l.map(x => (x.id === c.id ? { ...x, zone_bot: false, bot_reprise: r } : x)));
        if (activeConvRef.current === c.id && !blockedOnlyRef.current) setActiveConv(null);
        toast.success(`${c.name} sort de la Zone Bot (toujours bloqué)`);
        return;
      }
      if (activeConvRef.current === c.id) setActiveConv(null);
      toast.success(r.prevenir_au_shift ? `${c.name} → ${r.chatter_name} · Box ${r.box}, pour son prochain shift`
        : r.chatter_name ? `${c.name} rendu à ${r.chatter_name} · Box ${r.box}` : `${c.name} rendu aux chatteurs — personne en shift : les managers sont prévenus`);
      // Seulement si ce modèle est encore affiché : sinon sa liste s'affichait sous un autre modèle.
      rechargerListe(c.account_id);
    } catch (e) {
      toast.error(apiError(e, 'Impossible de rendre ce fan aux chatteurs'));
      // Souvent déjà rendu entre-temps (409) : Zone Bot et liste relues pour ne plus l'afficher.
      if (c.account_id === activeAccountRef.current) { chargerZoneRef.current(c.account_id, true); rechargerListe(c.account_id); }
    }
  }, [rechargerListe]);

  // 22/09 (Marlon) : « pouvoir les ajouter directement depuis l'appli, sans aller sur Telegram ». Coche la conversation
  // dans Telegram Business ET ajoute le fan à la liste du bot : il lui répondra dès son prochain message.
  const confierAuBot = useCallback(async (c) => {
    if (!c?.id) return;
    if (!window.confirm(`Confier ${c.name} au bot ?\n\nLe bot le coche dans Telegram Business et l'ajoute à sa liste : il lui répondra dès son prochain message. Tu peux le reprendre à tout moment depuis la page Bot Telegram (« Couper le bot »).`)) return;
    try {
      const { data } = await api.post('/bot-business/confier-au-bot', { conversation_id: c.id, activer: true });
      if (data?.ok) toast.success(`🤖 ${c.name} est confié au bot`);
      else toast.error('Telegram n’a pas confirmé l’ajout — réessaie ou ajoute-le dans Telegram');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec — réessaie'); }
  }, []);

  // C-547 (04/11) : le geste inverse — le bot ne lui écrit plus et il revient dans la messagerie des chatteuses.
  const rendreAuxChatteuses = useCallback(async (c) => {
    if (!c?.id) return;
    if (!window.confirm(`Rendre ${c.name} aux chatteuses ?\n\nLe bot ne lui écrira plus (ni réponse ni relance) et il revient dans la messagerie des chatteuses.`)) return;
    try {
      const { data } = await api.post('/bot-business/rendre-aux-chatteuses', { conversation_id: c.id });
      if (data?.ok) toast.success(`👩 ${c.name} est rendu aux chatteuses`);
      else toast.error('Échec — réessaie');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec — réessaie'); }
  }, []);

  const unblockConv = useCallback(async (c) => {
    const r = await patchConv(c.id, { blocked: false });
    if (r) {
      setBlockedConvs(l => l.filter(x => x.id !== c.id));
      if (activeConvRef.current === c.id) setActiveConv(null);  // il quitte « Bloqués » : sa conversation se ferme
      // Débloqué pendant la Zone Bot : il retourne dans la Zone Bot (cachée aux chatteurs), pas dans la liste.
      const enZone = r.id === c.id ? !!r.zone_bot : !!c.zone_bot;
      toast.success(enZone ? `${c.name} débloqué — il retourne dans la Zone Bot` : `${c.name} débloqué — il revient dans la liste`);
      rechargerListe(c.account_id);
      if (enZone) rafraichirZone(c.account_id);
    }
  }, [patchConv, rechargerListe, rafraichirZone]);

  const blockConv = useCallback(async (c) => {
    const r = await patchConv(c.id, { blocked: true });
    if (r) {
      // Use the ref (not activeConv) to keep this callback stable across chat switches.
      if (activeConvRef.current === c.id) setActiveConv(null);
      // E3 : sans « Bloqués » (chatteur), il ne pourra ni le retrouver ni le débloquer lui-même.
      toast.success(peutVoirBloques ? `${c.name} bloqué — masqué de la liste` : `${c.name} bloqué — un manager peut le débloquer`);
    }
  }, [patchConv, peutVoirBloques]);

  const markUnread = useCallback(async (c, e) => {
    e.stopPropagation();
    const r = await patchConv(c.id, { unread: 1 });
    if (r) toast.success('Marqué comme non lu');
  }, [patchConv]);

  const moveToBox = useCallback(async (c, b) => {
    const r = await patchConv(c.id, { box: b });
    if (r) toast.success(`Déplacé vers la Box ${b}`);
  }, [patchConv]);

  const patchAccount = async (patch) => {
    try {
      const { data } = await api.patch(`/accounts/${activeAccount}`, patch);
      setAccounts(prev => prev.map(a => a.id === data.id ? data : a));
      return data;
    } catch (e) { toast.error(apiError(e, 'Échec de la mise à jour')); return null; }
  };

  const toggleMute = async (a, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.patch(`/accounts/${a.id}`, { muted: !a.muted });
      setAccounts(prev => prev.map(x => x.id === data.id ? data : x));
      toast.success(data.muted ? `${a.name} mis en sourdine` : `${a.name} réactivé`);
    } catch (err) { toast.error(apiError(err, 'La mise à jour a échoué')); }
  };

  // Core send. Takes an EXPLICIT payload so it can be fired both from the composer
  // (current draft/pending state) and from the "/" slash menu (a picked script),
  // without relying on React state having flushed first.
  const doSend = async ({ text = '', vaults = [], script = null, reply = null }) => {
    // NOTE: deliberately do NOT gate on a `sending` flag here. The input/vaults
    // are cleared synchronously below, which already prevents a real double-send
    // of the SAME message — while letting a chatter fire several DIFFERENT
    // messages back-to-back without the button locking for ~1s per Telegram round-trip.
    if ((!text.trim() && vaults.length === 0) || !activeConv) return;
    const cid = activeConv;
    const nowIso = new Date().toISOString();
    const hhmm = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    // OPTIMISTIC UI — show the bubble INSTANTLY, send in the background, and
    // clear the input immediately so the chatter never waits on the server.
    const tempBase = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const optimistic = [];
    optimistic.push({
      id: `${tempBase}-0`, conversation_id: cid, from_: 'me', text,
      media: vaults[0] ? { name: vaults[0].name, type: vaults[0].type, url: vaults[0].url, tg_stored: true } : null,
      time: hhmm, created_at: nowIso, _pending: true,
      reply_to: reply ? { id: reply.id, name: reply.sender_name || (reply.from_ === 'me' ? 'Toi' : conv?.name || ''), text: (reply.text || '').slice(0, 80) } : null,
    });
    vaults.slice(1).forEach((v, i) => optimistic.push({
      id: `${tempBase}-${i + 1}`, conversation_id: cid, from_: 'me', text: '',
      media: { name: v.name, type: v.type, url: v.url, tg_stored: true },
      time: hhmm, created_at: nowIso, _pending: true,
    }));
    const tempIds = new Set(optimistic.map(o => o.id));

    // Write to BOTH the on-screen list and this fan's cache, keyed to `cid`. Every
    // later update (success/fail) is scoped to `cid` too, so a reply can NEVER bleed
    // into another fan's conversation if the chatter switches mid-send.
    setMessages(prev => [...prev, ...optimistic]);
    if (msgsCache.current[cid]) msgsCache.current[cid] = [...msgsCache.current[cid], ...optimistic];
    setConvs(prev => prev.map(c => c.id === cid ? {
      ...c,
      last_msg: text.slice(0, 80) || (vaults.length ? `📎 ${vaults[vaults.length - 1].name}` : ''),
      last_from: 'me', last_seen: false,
      sent_script_ids: script ? [...new Set([...(c.sent_script_ids || []), script.id])] : c.sent_script_ids,
      sent_vault_ids: vaults.length ? [...new Set([...(c.sent_vault_ids || []), ...vaults.map(v => v.id)])] : c.sent_vault_ids,
    } : c));
    setDraft(''); draftsRef.current[cid] = ''; setPendingScript(null); setPendingVaults([]); setReplyTo(null);

    try {
      const { data } = await api.post(`/conversations/${cid}/messages`, {
        text,
        script_id: script?.id || null,
        vault_ids: vaults.map(v => v.id),
        reply_to_id: reply?.id || null,
      });
      const real = Array.isArray(data) ? data : [data];
      const realIds = new Set(real.map(r => r.id));
      // Swap the optimistic bubbles for the confirmed ones (dedup vs any copy the
      // WebSocket may have pushed). Update THIS fan's cache always; touch the visible
      // list ONLY if the chatter is still on this fan — otherwise a slow response
      // would graft this fan's message onto whatever conversation they opened next
      // (the "envoyé à 2 fans" report).
      const swap = (arr) => [...arr.filter(m => !tempIds.has(m.id) && !realIds.has(m.id)), ...real];
      if (msgsCache.current[cid]) msgsCache.current[cid] = swap(msgsCache.current[cid]);
      if (activeConvRef.current === cid) setMessages(prev => swap(prev));
    } catch (e) {
      // BANNED WORD (400) — not a send failure: drop the optimistic bubble, put the
      // text BACK in the composer so the chatter can just edit out the word (no retype),
      // and show a clear French reason. No red "non envoyé" bubble (retrying the same
      // text would only re-trigger the block).
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 400 && det && typeof det === 'object' && det.code === 'banned_word') {
        const drop = (arr) => arr.filter(m => !tempIds.has(m.id));
        if (msgsCache.current[cid]) msgsCache.current[cid] = drop(msgsCache.current[cid]);
        if (activeConvRef.current === cid) { setMessages(prev => drop(prev)); if (text) setDraft(text); }
        toast.error(det.message || `Mot interdit : « ${det.word} »`, { duration: 6000 });
        return;
      }
      // AUTO-RETRY on 503 "account reconnecting": the backend raises 503 BEFORE any
      // Telegram send (so nothing was sent → zero double-send risk), purely because the
      // account had a brief proxy blip. Silently retry the SAME payload a few times with
      // backoff so it self-heals — this is exactly the "I had to rewrite the message 3×"
      // complaint. FloodWait (429) / real send errors (502) are NOT auto-retried.
      if (e?.response?.status === 503) {
        const payload = { text, script_id: script?.id || null, vault_ids: vaults.map(v => v.id), reply_to_id: reply?.id || null };
        // 5 tries over ~22s — long enough to cover an on-demand reconnect (the backend
        // kicks a fresh-IP rotation the moment we hit the 503), so the message lands by
        // itself instead of the chatter retrying for 10-15 min.
        for (let attempt = 1; attempt <= 5; attempt++) {
          await new Promise(r => setTimeout(r, attempt * 1500));
          try {
            const { data } = await api.post(`/conversations/${cid}/messages`, payload);
            const real = Array.isArray(data) ? data : [data];
            const realIds = new Set(real.map(r => r.id));
            const swap = (arr) => [...arr.filter(m => !tempIds.has(m.id) && !realIds.has(m.id)), ...real];
            if (msgsCache.current[cid]) msgsCache.current[cid] = swap(msgsCache.current[cid]);
            if (activeConvRef.current === cid) setMessages(prev => swap(prev));
            return; // recovered on its own — no red bubble, no retype
          } catch (e2) { e = e2; if (e2?.response?.status !== 503) break; }
        }
      }
      // Do NOT silently remove the bubble — that's the "it didn't send, I refreshed,
      // still nothing" complaint. Keep it, marked FAILED, so it shows red "non envoyé"
      // (with the text + media kept on the bubble for a retry) instead of vanishing.
      const markFail = (arr) => arr.map(m => tempIds.has(m.id)
        ? { ...m, _pending: false, _failed: true, _retry: { cid, text, vaultIds: vaults.map(v => v.id), scriptId: script?.id || null, replyId: reply?.id || null } }
        : m);
      if (msgsCache.current[cid]) msgsCache.current[cid] = markFail(msgsCache.current[cid]); // keep the red bubble for retry on return
      if (activeConvRef.current === cid) setMessages(prev => markFail(prev));
      const fw = extractFloodwait(e);
      if (fw) {
        toast.error(`⏳ FloodWait Telegram — ce compte doit attendre ${fw.seconds < 60 ? `${fw.seconds}s` : `${Math.round(fw.seconds / 60)}min`} avant d'envoyer à nouveau`, { duration: 8000 });
      } else if (det && typeof det === 'object' && det.message && ['peer_flood', 'blocked', 'deleted'].includes(det.code)) {
        // Differentiated Telegram errors → clear reason instead of raw "Too many requests".
        const icon = det.code === 'peer_flood' ? '⚠️' : det.code === 'blocked' ? '🚫' : '❌';
        toast.error(`${icon} ${det.message}`, { duration: 9000 });
      } else {
        toast.error(apiError(e, "Échec de l'envoi du message"));
      }
    }
  };

  // Découpe auto : un lien envoyé AVEC un vocal part en 2 messages — le LIEN d'abord
  // (bulle seule → aperçu automatique natif de Telegram, plus d'URL brute entassée),
  // puis le VOCAL avec le petit texte en légende. Demandé par Marlon (lien au-dessus,
  // texte collé au vocal). Ne touche PAS aux PPV photo/vidéo (lien en légende = normal).
  const splitLinkFromVoice = ({ text = '', vaults = [] }) => {
    const hasVoice = (vaults || []).some(v => v && v.type === 'voice');
    const urls = text.match(/https?:\/\/\S+/g) || [];
    if (!hasVoice || urls.length === 0) return null;
    const link = urls[urls.length - 1]; // le lien de paiement (ajouté en fin de texte)
    const rest = text.replace(link, '').replace(/[ \t]+\n/g, '\n').replace(/\n{2,}/g, '\n').trim();
    return { link, rest };
  };
  const doSendMaybeSplit = async (opts) => {
    const split = splitLinkFromVoice(opts);
    if (!split) return doSend(opts);
    // 1) le lien seul (aperçu auto), en attendant l'envoi pour garantir qu'il arrive AVANT
    await doSend({ text: split.link, vaults: [], script: null, reply: opts.reply });
    // 2) le vocal + la légende (sans le lien)
    return doSend({ text: split.rest, vaults: opts.vaults, script: opts.script, reply: null });
  };

  // Composer send (Enter / send button): fire whatever is currently staged.
  const send = () => doSendMaybeSplit({ text: draft, vaults: pendingVaults, script: pendingScript, reply: replyTo });

  // "/" slash menu pick: send the chosen script's body (+ link + its media) RIGHT AWAY,
  // to the active fan, then clear the composer. This is the Telegram-style instant flow.
  const sendSlashScript = (s) => {
    if (!s) return;
    const vs = (s.vault_ids || []).map(id => vault.find(x => x.id === id)).filter(Boolean);
    doSendMaybeSplit({ text: s.body + (s.link ? `\n${s.link}` : ''), vaults: vs, script: s, reply: replyTo });
  };

  // Re-send a bubble that failed (the chatter taps the red "non envoyé"). Re-uses the
  // exact payload captured at first send (text/media/script/reply + the ORIGINAL fan id),
  // so a retry can never go to a different fan either.
  const retrySend = async (m) => {
    const r = m && m._retry;
    if (!r) return;
    const cid = r.cid;                                   // ALWAYS the original fan
    const prefix = String(m.id).replace(/-\d+$/, '');
    const apply = (fn) => {
      if (msgsCache.current[cid]) msgsCache.current[cid] = fn(msgsCache.current[cid]);
      if (activeConvRef.current === cid) setMessages(prev => fn(prev));
    };
    apply(arr => arr.map(x => x.id.startsWith(prefix) ? { ...x, _failed: false, _pending: true } : x));
    try {
      const { data } = await api.post(`/conversations/${cid}/messages`, {
        text: r.text, script_id: r.scriptId, vault_ids: r.vaultIds, reply_to_id: r.replyId,
      });
      const real = Array.isArray(data) ? data : [data];
      const realIds = new Set(real.map(rr => rr.id));
      apply(arr => [...arr.filter(x => !x.id.startsWith(prefix) && !realIds.has(x.id)), ...real]);
    } catch (e) {
      apply(arr => arr.map(x => x.id.startsWith(prefix) ? { ...x, _pending: false, _failed: true } : x));
      toast.error(apiError(e, "Échec de l'envoi — réessaie"));
    }
  };

  const translateDraft = async (target) => {
    if (!draft.trim()) { toast.error("Écris d'abord un message"); return; }
    setTranslating(true);
    try {
      const { data } = await api.post('/translate', { text: draft, target });
      setDraft(data.translated);
      toast.success(`Traduit en ${target}`);
    } catch (e) { toast.error(apiError(e, 'Échec de la traduction')); } finally { setTranslating(false); }
  };

  const translateMessage = async (m) => {
    try {
      const { data } = await api.post('/translate', { text: m.text, target: 'French' });
      setTranslations(prev => ({ ...prev, [m.id]: data.translated }));
    } catch (e) { toast.error(apiError(e, 'Échec de la traduction')); }
  };

  const translateConversation = useCallback(async (c) => {
    setActiveConv(c.id);
    toast.info('Traduction des messages du fan en français…');
    try {
      const { data } = await api.get(`/conversations/${c.id}/messages`);
      const fanMsgs = data.filter(m => m.from_ === 'them' && m.text).slice(-6);
      const results = await Promise.all(fanMsgs.map(m => api.post('/translate', { text: m.text, target: 'French' }).then(r => [m.id, r.data.translated]).catch(() => null)));
      setTranslations(prev => ({ ...prev, ...Object.fromEntries(results.filter(Boolean)) }));
      toast.success('Conversation traduite');
    } catch (e) { toast.error(apiError(e, 'Échec de la traduction')); }
  }, []);

  const startReply = (m) => {
    setReplyTo({ id: m.id, name: m.from_ === 'me' ? 'Toi' : (conv?.name || 'Fan'), text: (m.text || m.media?.name || '').slice(0, 80) });
    inputRef.current?.focus();
  };

  const insertScript = (s) => {
    setDraft(s.body + (s.link ? `\n${s.link}` : ''));
    setPendingScript(s);
    if (s.vault_ids?.length) {
      const vs = s.vault_ids.map(id => vault.find(x => x.id === id)).filter(Boolean);
      if (vs.length) setPendingVaults(vs);
    }
    setScriptsOpen(false);
    inputRef.current?.focus();
  };

  const onlineCount = useMemo(() => convs.filter(c => c.online).length, [convs]);
  const unreadCount = useMemo(() => convs.filter(c => c.unread > 0).length, [convs]);
  // "Read, no reply" helper (isReadNoReply) is defined at module scope above.
  const readNoReplyCount = useMemo(() => convs.filter(isReadNoReply).length, [convs]);
  const boxesEnabled = account?.boxes_enabled !== false;
  // Only managers/owner (msg_manage_boxes) may reshuffle fans between boxes — a plain
  // chatter must not change the box distribution. Backend enforces this too.
  const canMoveBox = hasPerm('msg_manage_boxes');
  // E5 : fans à qui le bot écrit en ce moment (moins de 30 min) ; l'étiquette disparaît toute seule, sans recharger.
  const [horlogeBot, setHorlogeBot] = useState(0);
  const botParleIds = useMemo(() => {
    const maintenant = Date.now();
    return new Set([...convs, ...blockedConvs].filter(c => botParleReste(c, maintenant) > 0).map(c => c.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convs, blockedConvs, horlogeBot]);
  useEffect(() => {
    const maintenant = Date.now();
    const restes = [...convs, ...blockedConvs].map(c => botParleReste(c, maintenant)).filter(ms => ms > 0);
    if (!restes.length) return undefined;
    const t = setTimeout(() => setHorlogeBot(h => h + 1), Math.min(...restes) + 250);
    return () => clearTimeout(t);
  }, [convs, blockedConvs, horlogeBot]);

  // "/" slash-command menu: type "/" (then optionally letters) in the composer to
  // filter scripts inline. Title matches rank above tag/body; capped so a big
  // library stays snappy (the menu scrolls). Picking one SENDS it immediately.
  const slashQuery = (draft.startsWith('/') && draft !== slashDismissed) ? draft.slice(1).trim().toLowerCase() : null;
  const slashMatches = useMemo(() => {
    if (slashQuery === null) return [];
    const q = slashQuery;
    // Match the script NAME or its TAG only — NOT the body (body matches mixed in
    // unrelated scripts). So "/bienvenue" returns the whole "bienvenue" set.
    const arr = scripts.filter(s => !q
      || (s.title || '').toLowerCase().includes(q)
      || (s.tag || '').toLowerCase().includes(q));
    // Group by tag so scripts of the same family stay together (not mixed); a
    // tag-name match ranks first, then alphabetical by tag, then by title.
    arr.sort((a, b) => {
      const at = (a.tag || '').toLowerCase(), bt = (b.tag || '').toLowerCase();
      if (q) {
        const aTag = at.includes(q) ? 0 : 1, bTag = bt.includes(q) ? 0 : 1;
        if (aTag !== bTag) return aTag - bTag;
      }
      if (at !== bt) return at < bt ? -1 : 1;
      // Dans un même tag : respecter l'ORDRE défini (champ `order`), pas le titre —
      // certains titres ont un espace en tête (" 07 …") qui les faisait remonter à tort.
      const ao = Number.isFinite(a.order) ? a.order : 1e9, bo = Number.isFinite(b.order) ? b.order : 1e9;
      if (ao !== bo) return ao - bo;
      return (a.title || '').localeCompare(b.title || '');
    });
    return arr.slice(0, 80);
  }, [slashQuery, scripts]);
  const slashOpen = slashQuery !== null && slashMatches.length > 0;
  useEffect(() => { setSlashIdx(0); }, [slashQuery]);
  // Smart lists with a structured rule are evaluated live against each conversation
  const spentByConv = useMemo(() => {
    const m = {};
    // Exclude reversed sales (rejected/refunded/withdrawn) — they must not push a
    // fan into a VIP/Top-spender list. Mirrors the backend's c.spent accounting.
    const REVERSED = new Set(['rejected', 'refunded', 'withdrawn']);
    allSales.forEach(s => {
      if (s.conversation_id && !REVERSED.has(s.status))
        m[s.conversation_id] = (m[s.conversation_id] || 0) + (s.amount || 0);
    });
    return m;
  }, [allSales]);
  // Évalue la RÈGLE d'une smart list contre une conv (sans pins ni paliers).
  const evalRule = (sl, c) => {
    if (!sl || !sl.rule_field || !sl.rule_op) return false;
    const days = (iso) => (iso ? (Date.now() - new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(String(iso)) ? iso : String(iso) + 'Z').getTime()) / 86400000 : null);
    const val = sl.rule_field === 'days_inactive' ? days(c.last_activity)
      : sl.rule_field === 'fan_age_days' ? days(c.created_at || c.last_activity)
      : (spentByConv[c.id] || 0);
    if (val === null) return false;
    const t = Number(sl.rule_value);
    if (sl.rule_op === 'between') { const t2 = Number(sl.rule_value2); return val >= Math.min(t, t2) && val <= Math.max(t, t2); }
    return sl.rule_op === '>=' ? val >= t : sl.rule_op === '<=' ? val <= t : Math.round(val) === t;
  };
  // Plancher d'un palier de dépense (pour garder le plus haut atteint).
  const spendFloor = (sl) => sl.rule_op === 'between' ? Math.min(Number(sl.rule_value), Number(sl.rule_value2)) : Number(sl.rule_value);
  // Étiquettes de DÉPENSE exclusives : le fan ne garde QUE le palier le plus haut atteint
  // (ex. ≥500 € → VIP seul, pas Baleine en plus). Renvoie l'id du palier gagnant (ou null).
  const topSpendId = (c) => {
    let best = null;
    smartLists.forEach(sl => {
      if (sl.rule_field === 'total_spent' && sl.rule_op && (evalRule(sl, c) || (c.lists || []).includes(sl.id))) {
        if (!best || spendFloor(sl) > spendFloor(best)) best = sl;
      }
    });
    return best ? best.id : null;
  };
  const listMatch = (c, lid) => {
    const sl = smartLists.find(l => l.id === lid);
    if (sl && sl.rule_field && sl.rule_op) {
      const isMember = evalRule(sl, c) || (c.lists || []).includes(lid); // règle OU ajout manuel
      if (!isMember) return false;
      if (sl.rule_field === 'total_spent') return topSpendId(c) === lid; // exclusivité des paliers
      return true;
    }
    return (c.lists || []).includes(lid);
  };
  const filteredNormal = useMemo(() => {
    const f = convs.filter(c =>
      (!boxesEnabled || box === 0 || c.box === box) &&
      (!onlineOnly || c.online) &&
      (!unreadOnly || c.unread > 0) &&
      (!readNoReplyOnly || isReadNoReply(c)) &&
      (!listFilter || (listFilter === LIEN_ENVOYE ? aRecuLienBot(c) : listMatch(c, listFilter))) &&
      correspondRecherche(c, q)
    );
    const base = oldestFirst ? [...f].sort((a, b) => new Date(a.last_activity) - new Date(b.last_activity)) : f;
    // V6 : pastille « Lien envoyé » → 🟢 puis 🟡 en tête (tri stable : l'ordre choisi est gardé dans chaque groupe).
    if (listFilter === LIEN_ENVOYE) return [...base].sort((a, b) => rangLienEnvoye(a) - rangLienEnvoye(b));
    if (oldestFirst) return base;
    // V5 : tri par défaut → fans 🟢 puis 🟡 du bot en haut (3 jours) ; les autres gardent leur ordre.
    return f.some(c => rangClassement(c) < 2) ? [...f].sort((a, b) => rangClassement(a) - rangClassement(b)) : f;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convs, boxesEnabled, box, onlineOnly, unreadOnly, readNoReplyOnly, listFilter, q, oldestFirst, smartLists, spentByConv]);

  // En mode « Bloqués » on montre CES conversations-là, les plus gros payeurs en
  // premier : c'est là que se cachent les blocages qui ont coûté de l'argent.
  const filtered = useMemo(() => {
    if (zoneOnly) {
      const t = q.trim().toLowerCase();
      // 04/10 (B-379, Marlon) : « j'aimerais pouvoir trier les fans, comme ça je vois rapidement ceux
      // sur lesquels le bot est en train de parler ou bien qui n'ont pas encore eu de réponse. SURTOUT
      // les fans sans réponse. » Ordre : ⏳ sans réponse d'abord (le dernier message est du fan), du
      // plus récent au plus ancien ; puis ceux à qui le bot a déjà répondu ; les 🔴 time wasters en bas.
      return zoneConvs.filter(c => !t || correspondRecherche(c, t))
        .sort((a, b) => (estTimeWaster(a) ? 1 : 0) - (estTimeWaster(b) ? 1 : 0)
          || (sansReponse(b) ? 1 : 0) - (sansReponse(a) ? 1 : 0)
          || new Date(b.last_activity) - new Date(a.last_activity));
    }
    if (!blockedOnly) return filteredNormal;
    const t = q.trim().toLowerCase();
    return [...blockedConvs]
      .filter(c => !t || correspondRecherche(c, t))
      .sort((a, b) => (b.spent || 0) - (a.spent || 0) || (b.unread || 0) - (a.unread || 0));
  }, [blockedOnly, blockedConvs, filteredNormal, q, zoneOnly, zoneConvs]);
  // Keep the conversation list cheap to render: show the first `visibleCount` rows and load
  // 60 more as you scroll. Rendering all 600+ rows (each a context-menu component) at once
  // was the main cause of the load lag. Reset to the top when the filtered set changes.
  useEffect(() => { setVisibleCount(60); }, [q, listFilter, box, onlineOnly, unreadOnly, readNoReplyOnly, oldestFirst, activeAccount]);
  // Changer de modèle en mode « Bloqués » doit recharger LES SIENNES.
  useEffect(() => { if (blockedOnly && activeAccount) chargerBloquees(activeAccount); },
    [blockedOnly, activeAccount, chargerBloquees]);
  const onConvListScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 500 && visibleCount < filtered.length) {
      setVisibleCount(v => v + 60);
    }
  };
  // Render only the last 80 message bubbles (opening a chat rendered all ~500 at once,
  // a ~1s block). Reset on chat switch; load 80 older when you scroll near the top,
  // preserving scroll position so it doesn't jump.
  const onMsgScroll = (e) => {
    const el = e.currentTarget;
    // 15/09 : revenu en bas → le fil se recolle ; remonté À LA MAIN → il ne redescend plus tout seul.
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) collerEnBasRef.current = true;
    else if (Date.now() - gesteFilRef.current < 1500) collerEnBasRef.current = false;
    if (el.scrollTop < 150 && visibleMsgCount < messages.length) {
      const prevH = el.scrollHeight;
      setVisibleMsgCount(v => v + 80);
      requestAnimationFrame(() => { if (msgScrollRef.current) msgScrollRef.current.scrollTop += msgScrollRef.current.scrollHeight - prevH; });
    }
  };
  const usedListIds = useMemo(() => Array.from(new Set(convs.flatMap(c => c.lists || []))), [convs]);
  // Dynamic smart-list membership per conversation, shown as badges like manual lists.
  // Memoized so it doesn't recompute on every render (keeps the inbox fluid).
  // Per-conv array refs are cached (badgeCacheRef): when only `convs` changes for an
  // unrelated reason (e.g. opening a chat resets its unread → new convs array), each
  // conv whose badge SET is unchanged keeps its previous array reference, so ConvRow's
  // React.memo holds and the other ~58 rows don't re-render on a chat switch.
  const badgeCacheRef = useRef({ smartLists: null, spentByConv: null, cache: {} });
  const smartBadgesByConv = useMemo(() => {
    const ruleLists = smartLists.filter(sl => sl.rule_field && sl.rule_op);
    // 14/10 (B-859, Jean Yves) : les étiquettes n'étaient calculées que pour la liste normale ; un fan
    // de la Zone Bot ou des Bloqués qui avait payé n'avait AUCUNE étiquette (pas de « Spenders »). On les
    // calcule pour les trois listes ; le seuil des 40 % reste mesuré sur la liste normale seule.
    const vus = new Set();
    const toutes = [...convs, ...zoneConvs, ...blockedConvs].filter(c => c && c.id && !vus.has(c.id) && vus.add(c.id));
    if (!ruleLists.length || !toutes.length) return {};
    const normales = new Set(convs.map(c => c.id));
    const now = Date.now();
    const daysOf = (iso) => (iso ? (now - new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(String(iso)) ? iso : String(iso) + 'Z').getTime()) / 86400000 : null);
    // Precompute each conversation's 3 metrics ONCE. Re-parsing dates per rule, for 600+
    // chats × every smart list, on every refresh/incoming message, was the real cause of
    // the UI lag (a ~850ms main-thread block each time). Now the dates are parsed once.
    const metrics = toutes.map(c => ({
      id: c.id,
      days_inactive: daysOf(c.last_activity),
      fan_age_days: daysOf(c.created_at || c.last_activity),
      spent: spentByConv[c.id] || 0,
      lists: c.lists || [],
    }));
    const hit = (m, sl) => {
      if (m.lists && m.lists.includes(sl.id)) return true; // ajout manuel = override de la règle
      const val = sl.rule_field === 'days_inactive' ? m.days_inactive
        : sl.rule_field === 'fan_age_days' ? m.fan_age_days : m.spent;
      if (val === null) return false;
      const t = Number(sl.rule_value);
      if (sl.rule_op === 'between') { const t2 = Number(sl.rule_value2); return val >= Math.min(t, t2) && val <= Math.max(t, t2); }
      return sl.rule_op === '>=' ? val >= t : sl.rule_op === '<=' ? val <= t : Math.round(val) === t;
    };
    const total = normales.size;
    // Auto-hide smart lists that match too broadly (>40% of conversations).
    const discriminating = ruleLists.filter(sl => {
      if (!total) return true;
      let n = 0; for (const m of metrics) if (normales.has(m.id) && hit(m, sl)) n++;
      return n / total <= 0.4;
    });
    // Reset the per-conv ref cache only when the badge DEFINITIONS or spend change.
    const bc = badgeCacheRef.current;
    if (bc.smartLists !== smartLists || bc.spentByConv !== spentByConv) {
      badgeCacheRef.current = { smartLists, spentByConv, cache: {} };
    }
    const cache = badgeCacheRef.current.cache;
    const floor = (sl) => sl.rule_op === 'between' ? Math.min(Number(sl.rule_value), Number(sl.rule_value2)) : Number(sl.rule_value);
    const map = {};
    for (const m of metrics) {
      let matched = discriminating.filter(sl => hit(m, sl));
      // Étiquettes de DÉPENSE exclusives : on ne garde que le palier le plus haut atteint
      // (ex. ≥500 € → VIP seul, pas Baleine + VIP).
      const spend = matched.filter(sl => sl.rule_field === 'total_spent');
      if (spend.length > 1) {
        const top = spend.reduce((a, b) => floor(b) > floor(a) ? b : a);
        matched = matched.filter(sl => sl.rule_field !== 'total_spent' || sl.id === top.id);
      }
      if (!matched.length) continue;
      const sig = matched.map(s => s.id).join(',');
      const prev = cache[m.id];
      if (prev && prev.sig === sig) { map[m.id] = prev.val; } // reuse stable ref
      else { cache[m.id] = { sig, val: matched }; map[m.id] = matched; }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convs, zoneConvs, blockedConvs, smartLists, spentByConv]);
  // Filter chips: rule-based smart lists are always offered; manual lists only when used
  const filterChipIds = useMemo(() => {
    const ruleLists = smartLists.filter(l => l.rule_field && l.rule_op).map(l => l.id);
    const ids = Array.from(new Set([...ruleLists, ...usedListIds]));
    // « Nouveaux fans » toujours en PREMIÈRE position (le reste garde son ordre).
    const isNew = (id) => /nouveaux?\s*fans/i.test((allLists.find(x => x.id === id) || {}).name || '');
    return ids.sort((a, b) => (isNew(a) ? 0 : 1) - (isNew(b) ? 0 : 1));
  }, [smartLists, usedListIds, allLists]);
  // V6 : pastille « 🤖 Lien envoyé » JUSTE APRÈS « Nouveaux fans » (en premier s'il n'y en a pas), pour tout le monde :
  // dès qu'un fan rendu par le bot est dans la liste (ou, pour un manager, sur le compte du bot).
  const pastilleLienEnvoye = useMemo(() => !!zoneBot?.bot || convs.some(aRecuLienBot), [zoneBot, convs]);
  const chipsListe = useMemo(() => {
    if (!pastilleLienEnvoye) return filterChipIds;
    const i = filterChipIds.findIndex(id => /nouveaux?\s*fans/i.test((allLists.find(x => x.id === id) || {}).name || ''));
    return [...filterChipIds.slice(0, i + 1), LIEN_ENVOYE, ...filterChipIds.slice(i + 1)];
  }, [filterChipIds, pastilleLienEnvoye, allLists]);
  useEffect(() => { if (listFilter === LIEN_ENVOYE && !pastilleLienEnvoye) setListFilter(''); }, [listFilter, pastilleLienEnvoye]);

  const reorderAccounts = (targetId) => {
    const from = dragAccId.current; dragAccId.current = null;
    if (!from || from === targetId) return;
    setAccounts(prev => {
      const arr = [...prev];
      const fi = arr.findIndex(a => a.id === from), ti = arr.findIndex(a => a.id === targetId);
      if (fi < 0 || ti < 0) return prev;
      const [m] = arr.splice(fi, 1); arr.splice(ti, 0, m);
      api.post('/accounts/reorder', { ordered_ids: arr.map(a => a.id) }).catch(() => {});
      return arr;
    });
  };
  const changeBoxCount = async (n) => {
    const r = await patchAccount({ boxes: n });
    if (r) { toast.success(`${n} boxes — conversations rééquilibrées`); setBox(0); loadConvs(activeAccount, 'keep'); }
  };

  // Per-fan: which SENT scripts have been PAID (an approved sale in this conv whose amount
  // matches the script's price). When a sent message carries a script_id we disambiguate
  // same-price scripts by picking the one sent closest BEFORE the sale.
  // 15/09 (Marlon) : le bot d'accueil a déjà fait la découverte et envoyé la photo du script « Bienvenue » → pour un fan passé
  // par le bot, ce(s) script(s) sont grisés « déjà fait par le bot » (toujours utilisables). Aucun réglage.
  // 18/09 (Marlon) : le bot va jusqu'à SON lien de paiement → on grise la bienvenue ET le PPV qu'il a vendu,
  // et ce PPV passe en ✓ PAYÉ tout seul dès que la vente du lien du bot arrive. Le chatteur ne renvoie plus le même lien.
  const duBot = useMemo(() => scriptsDuBot({
    scripts,
    ventes: convSales,
    venuDuBot: !!(conv && (conv.zone_bot || conv.bot_reprise || conv.bot_lien || conv.bot_classement)),
  }), [conv, scripts, convSales]);
  const scriptsBienvenueBot = duBot.faits;
  const paidScriptIdsVentes = useMemo(() => {
    // Démarre avec les scripts marqués payés via les notes Telegram importées (✅),
    // puis ajoute ceux confirmés par une vraie vente ChatHub.
    const sentIds = conv?.sent_script_ids || [];
    const priceById = {};
    scripts.forEach(s => { priceById[s.id] = s.price || 0; });
    const sendDates = {};
    messages.forEach(m => { if (m.script_id) { (sendDates[m.script_id] = sendDates[m.script_id] || []).push(new Date(m.created_at)); } });
    // #156 : le fan paie souvent un peu plus que le prix du script (frais Dropp) → marge, plus le prix le plus proche.
    return scriptsPayes({
      dejaPayes: conv?.paid_script_ids || [],
      envoyes: sentIds,
      prixParId: priceById,
      datesEnvoi: sendDates,
      ventes: convSales,
    });
  }, [conv, scripts, messages, convSales]);
  // Le PPV vendu par le bot d'accueil n'a jamais été « envoyé » par un chatteur : il est payé quand même.
  const paidScriptIds = useMemo(() => {
    if (!duBot.payes.length) return paidScriptIdsVentes;
    const tous = new Set(paidScriptIdsVentes);
    duBot.payes.forEach(id => tous.add(id));
    return tous;
  }, [paidScriptIdsVentes, duBot]);

  // Zone Bot et Bloqués s'excluent. En changeant de vue, le fan ouvert qui n'est plus dans la liste affichée est fermé
  // (sinon le sondage continuait de le marquer lu en arrière-plan, sans que personne ne le regarde).
  const changerVue = (vue) => {
    const zone = vue === 'zone';
    if (activeConv && !convs.some(c => c.id === activeConv) && !(zone && zoneConvs.some(c => c.id === activeConv))) setActiveConv(null);
    setZoneOnly(zone);
    setBlockedOnly(vue === 'bloques' && peutVoirBloques);
    if (zone) chargerZone(activeAccount);
    // Hors de la Zone Bot, sa liste n'est plus relue : on l'oublie (sinon une action sur un fan déjà rendu baissait le compteur).
    else { zoneConvsRef.current = []; setZoneConvs([]); }
    if (vue === 'bloques' && peutVoirBloques) chargerBloquees(activeAccount);
  };
  // Zone Bot et Bloqués ont leur propre liste : les filtres de la liste normale ne s'y appliquent pas (grisés).
  const horsListe = zoneOnly || blockedOnly;
  const filtresGrises = horsListe ? ' opacity-40 pointer-events-none' : '';

  const renderConv = (c) => (
    <ConvRow
      key={c.id}
      c={c}
      isActive={activeConv === c.id}
      botParle={botParleIds.has(c.id)}
      classementToujours={listFilter === LIEN_ENVOYE}
      badges={smartBadgesByConv[c.id]}
      allLists={allLists}
      manualLists={taggableLists}
      toggleFanList={toggleFanList}
      boxesEnabled={boxesEnabled}
      boxCount={boxCount}
      canMoveBox={canMoveBox}
      setActiveConv={setActiveConv}
      prefetchConv={prefetchConv}
      inputRef={inputRef}
      markUnread={markUnread}
      translateConversation={translateConversation}
      togglePin={togglePin}
      setRenaming={setRenaming}
      setAddToList={setAddToList}
      moveToBox={moveToBox}
      blockConv={blockConv}
      rendreAuxChatteurs={rendreAuxChatteurs}
      venteLe={derniereVenteParConv.get(c.id) || 0}
      retirerClassement={peutRetirerClassement ? retirerClassement : undefined}
    />
  );

  return (
    <div className="h-screen flex flex-col" data-testid="inbox-page">
      <OnboardingTour key={inboxTourKey} tour="inbox" forceOpen={inboxTourKey > 0} />
      {/* Account tabs */}
      <div data-tour="inbox-accounts" className="border-b border-white/5 px-4 py-2 flex items-center gap-2 overflow-x-auto scrollbar-thin shrink-0">
        <button
          data-testid="inbox-replay-tour"
          onClick={() => { replayInboxTour(); setInboxTourKey(k => k + 1); }}
          title="Revoir le guide du Messaging"
          className="shrink-0 order-last flex items-center gap-1.5 text-xs text-slate-400 hover:text-indigo-300 hover:bg-white/5 rounded-lg px-2.5 py-1.5 border border-white/10"
        >
          <HelpCircle size={14} /> <span className="hidden sm:inline">Guide</span>
        </button>
        {accounts.map(a => (
          <div key={a.id} draggable onDragStart={() => { dragAccId.current = a.id; }} onDragOver={e => e.preventDefault()} onDrop={() => reorderAccounts(a.id)} className={`cursor-grab active:cursor-grabbing flex items-center gap-1 rounded-xl border ${activeAccount === a.id ? 'bg-sky-500/15 border-sky-400/40' : 'border-white/5 bg-white/[0.02] hover:bg-white/5'}`}>
            <button data-testid={`inbox-account-${a.id}`} onClick={() => setActiveAccount(a.id)}
              className={`flex items-center gap-2 pl-3 pr-1 py-2 text-sm whitespace-nowrap ${activeAccount === a.id ? 'text-white font-medium' : 'text-slate-400'}`}>
              {a.profile_photo_url ? (
                <img src={a.profile_photo_url.startsWith('http') ? a.profile_photo_url : `${process.env.REACT_APP_BACKEND_URL}${a.profile_photo_url}`} alt={a.name} className="w-6 h-6 rounded-lg object-cover" />
              ) : (
                <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${a.color} flex items-center justify-center text-[10px] text-white font-bold`}>{a.initial}</div>
              )}
              {a.name}
              {a.unread > 0 && !a.muted && <Badge className="bg-rose-500 text-white border-0 px-1.5 py-0 text-[9px] rounded-full">{a.unread}</Badge>}
            </button>
            <button data-testid={`inbox-mute-${a.id}`} onClick={(e) => toggleMute(a, e)} className="pr-2 pl-0.5 py-2 text-slate-500 hover:text-white" title={a.muted ? 'Réactiver le son' : 'Mettre en sourdine'}>
              {a.muted ? <BellOff size={13} className="text-rose-400" /> : <Bell size={13} />}
            </button>
          </div>
        ))}
        <ChannelFeedButton account={activeAccount} />
        {(() => {
          const pinned = filtered.filter(c => c.pinned);
          if (!pinned.length) return null;
          return (
            <>
              <div className="flex-1 min-w-[8px]" />
              <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-300 shrink-0" data-testid="pinned-label">
                <Pin size={11} className="fill-indigo-400 text-indigo-400 -rotate-45" /> Épinglées
              </span>
              {pinned.map(c => (
                <button key={c.id} onClick={() => setActiveConv(c.id)} onContextMenu={(e) => { e.preventDefault(); togglePin(c); }} title={`${c.name} — clic droit pour désépingler`} data-testid="pinned-chip"
                  className={`shrink-0 flex items-center gap-1.5 rounded-xl border px-2 py-1.5 transition-colors ${activeConv === c.id ? 'bg-indigo-500/25 border-indigo-500/50' : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06]'}`}>
                  <div className="relative shrink-0">
                    <ConvAvatar name={c.name} url={c.profile_photo_url} size="w-6 h-6" textSize="text-[9px]" />
                    {tgStatus(c).online && <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 border border-[#0c0d14]" />}
                  </div>
                  <span className="text-[12px] font-medium text-white whitespace-nowrap max-w-[100px] truncate">{c.name}</span>
                  {c.unread > 0 && <span className="min-w-[15px] h-4 px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center shrink-0" style={{ background: '#3f7dff' }}>{c.unread}</span>}
                </button>
              ))}
            </>
          );
        })()}
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Conversation list — plein écran sur mobile, colonne fixe sur ordi. Masquée sur
            mobile quand une conversation est ouverte (on montre le chat à la place). */}
        <div data-tour="inbox-conv-list" className={`w-full md:w-[340px] border-r border-white/5 flex-col shrink-0 bg-[#0C0C0F] ${activeConv ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-2.5 space-y-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input data-testid="inbox-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher une conversation… ou @pseudo" className="bg-[#18171C] border-white/10 text-white pl-9 h-10 text-sm rounded-xl" />
            </div>

            {zoneBot?.bot && (
              <button data-testid="inbox-zone-bot" onClick={() => changerVue(zoneOnly ? 'normal' : 'zone')}
                className={`w-full flex items-center justify-between rounded-xl border px-3 py-2 text-xs ${zoneOnly ? 'bg-violet-500/20 border-violet-400/50 text-violet-100' : 'bg-violet-500/[0.06] border-violet-500/25 text-violet-200 hover:bg-violet-500/10'}`}>
                <span className="flex items-center gap-1.5 font-medium"><Bot size={13} /> Zone Bot</span>
                <span className="flex items-center gap-2">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/25" data-testid="zone-bot-count">{zoneBot.nb_zone || 0}</span>
                  <span className="text-[10px] text-violet-300/80">{zoneOnly ? 'Retour à la liste' : 'Voir'}</span>
                </span>
              </button>
            )}
            {zoneOnly && (
              <div className="rounded-lg border border-violet-500/30 bg-violet-500/[0.07] px-2.5 py-1 text-[11px] text-violet-100" data-testid="zone-bot-bandeau">
                <div className="flex items-center gap-1.5">
                  <span className="truncate">Nouveaux fans avec le bot, cachés aux chatteurs</span>
                  <button type="button" data-testid="zone-bot-info" onClick={() => setInfoZone(v => !v)} aria-label="Explication de la Zone Bot"
                    title="Ils reviennent après le lien du bot (🟢, 🟡) ou au bout de 3 jours. Les 🔴 sont sur la page Bot d'accueil."
                    className="ml-auto shrink-0 w-4 h-4 rounded-full border border-violet-300/50 text-[10px] leading-none text-violet-200 hover:bg-violet-500/25">i</button>
                </div>
                {infoZone && <div className="mt-1 text-violet-200/90" data-testid="zone-bot-info-texte">Les deux bots sont ici. <b>🤖 bot HERA</b> : les fans que le bot Telegram tient en ce moment, il leur répond lui-même. <b>🤖 avec le bot</b> : le bot d&apos;accueil — ils reviennent après son lien (🟢, 🟡) ou au bout de 3 jours. Les 🔴 sont sur la page Bot d&apos;accueil.</div>}
              </div>
            )}

            {blockedOnly && (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.07] px-3 py-2 text-[11px] text-rose-100">
                Tu vois les <b>conversations bloquées</b>, celles qui ont le plus dépensé en premier.
                Elles sont invisibles pour les chatteurs tant qu'elles restent bloquées.
              </div>
            )}

            {/* Filter toggles — like QG Chatting. « Bloqués » : seulement pour qui voit les bloqués (managers). */}
            <div className="grid grid-cols-2 gap-x-2 gap-y-2.5 rounded-xl border border-white/5 bg-white/[0.02] p-3">
              {peutVoirBloques && (
              <label className="flex items-center gap-2 cursor-pointer col-span-2 border-b border-white/5 pb-2.5">
                <Switch data-testid="filter-blocked" checked={blockedOnly}
                  onCheckedChange={(v) => changerVue(v ? 'bloques' : 'normal')}
                  className="data-[state=checked]:bg-rose-500 scale-90" />
                <span className="text-xs text-rose-300 flex items-center gap-1"><Ban size={12} /> Bloqués</span>
                {blockedOnly && <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300" data-testid="blocked-count">{blockedConvs.length}</span>}
              </label>
              )}
              <label className={`flex items-center gap-2 cursor-pointer${filtresGrises}`}>
                <Switch data-testid="filter-read-no-reply" checked={readNoReplyOnly} onCheckedChange={setReadNoReplyOnly} disabled={horsListe} className="data-[state=checked]:bg-amber-500 scale-90" />
                <span className="text-xs text-amber-300 flex items-center gap-1"><CornerUpLeft size={12} /> Sans réponse</span>
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300" data-testid="read-no-reply-count">{readNoReplyCount}</span>
              </label>
              <label className={`flex items-center gap-2 cursor-pointer${filtresGrises}`}>
                <Switch data-testid="filter-online" checked={onlineOnly} onCheckedChange={setOnlineOnly} disabled={horsListe} className="data-[state=checked]:bg-emerald-500 scale-90" />
                <span className="text-xs text-emerald-300 flex items-center gap-1"><Zap size={12} /> En ligne</span>
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">{onlineCount}</span>
              </label>
              <label className={`flex items-center gap-2 cursor-pointer${filtresGrises}`}>
                <Switch data-testid="filter-unread" checked={unreadOnly} onCheckedChange={setUnreadOnly} disabled={horsListe} className="data-[state=checked]:bg-violet-500 scale-90" />
                <span className="text-xs text-violet-300 flex items-center gap-1"><Mail size={12} /> Non lus</span>
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300">{unreadCount}</span>
              </label>
              <label className={`flex items-center gap-2 cursor-pointer${filtresGrises}`}>
                <Switch data-testid="filter-oldest-first" checked={oldestFirst} onCheckedChange={setOldestFirst} disabled={horsListe} className="data-[state=checked]:bg-violet-500 scale-90" />
                <span className="text-xs text-violet-300 flex items-center gap-1"><Clock size={12} /> Plus anciens d&apos;abord</span>
              </label>
            </div>

            {/* Box distribution */}
            <div data-tour="inbox-boxes" className="rounded-xl border border-white/5 bg-white/[0.02]">
              <button data-testid="box-distribution-toggle" onClick={() => setBoxPanelOpen(o => !o)} className="w-full flex items-center justify-between px-3 py-2.5">
                <span className="text-xs font-medium text-slate-200">Répartition des boxes</span>
                <span className="flex items-center gap-2 text-[11px] text-slate-500">{boxesEnabled ? 'Activé' : 'Désactivé'} {boxPanelOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}</span>
              </button>
              {boxPanelOpen && (
                <div className="px-3 pb-3 space-y-2.5 border-t border-white/5 pt-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-300">Activer les boxes</span>
                    <Switch data-testid="enable-boxes-switch" checked={boxesEnabled} onCheckedChange={v => { patchAccount({ boxes_enabled: v }); if (!v) setBox(0); }} className="data-[state=checked]:bg-indigo-500" />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-slate-300">Nombre</span>
                    <select data-testid="boxes-number-select" value={boxCount} onChange={e => changeBoxCount(parseInt(e.target.value))} disabled={!boxesEnabled} className="flex-1 max-w-[120px] bg-[#18171C] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white outline-none disabled:opacity-40">
                      {[2, 3, 4, 5, 6, 7, 8, 9].map(n => <option key={n} value={n} className="bg-[#0B0B0E]">{n}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {chipsListe.length > 0 && (
              <div className={`flex gap-1 overflow-x-auto scrollbar-thin${filtresGrises}`} data-testid="filtres-listes">
                <button onClick={() => setListFilter('')} className={`px-2.5 py-1 rounded-full text-[10px] whitespace-nowrap ${!listFilter ? 'bg-white text-slate-900 font-semibold' : 'bg-white/5 text-slate-400'}`}>Tout</button>
                {chipsListe.map(lid => {
                  if (lid === LIEN_ENVOYE) {
                    return (
                      <button key={lid} data-testid="list-filter-lien-envoye" title="Fans rendus par le bot après son lien : 🟢 puis 🟡" onClick={() => setListFilter(listFilter === lid ? '' : lid)} className={`px-2.5 py-1 rounded-full text-[10px] whitespace-nowrap border ${listFilter === lid ? 'bg-violet-500/20 text-violet-200 border-violet-500/40' : 'border-transparent bg-white/5 text-slate-500'}`}>
                        🤖 Lien envoyé
                      </button>
                    );
                  }
                  const l = allLists.find(x => x.id === lid);
                  if (!l) return null;
                  return (
                    <button key={lid} data-testid={`list-filter-${lid}`} onClick={() => setListFilter(listFilter === lid ? '' : lid)} className={`px-2.5 py-1 rounded-full text-[10px] whitespace-nowrap border ${listFilter === lid ? l.color : 'border-transparent bg-white/5 text-slate-500'}`}>
                      {l.emoji || '●'} {l.name}
                    </button>
                  );
                })}
              </div>
            )}
            {boxesEnabled && (
              <div className={`flex gap-1 overflow-x-auto scrollbar-thin${filtresGrises}`} data-testid="filtres-boxes">
                <button onClick={() => setBox(0)} className={`px-2.5 py-1 rounded-lg text-[10px] whitespace-nowrap ${box === 0 ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-400'}`}>Toutes les boxes</button>
                {Array.from({ length: boxCount }, (_, i) => i + 1).map(b => (
                  <button key={b} data-testid={`inbox-box-${b}`} onClick={() => setBox(b)} className={`px-2.5 py-1 rounded-lg text-[10px] whitespace-nowrap ${box === b ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-400'}`}>Box {b}</button>
                ))}
              </div>
            )}
          </div>
          <div onScroll={onConvListScroll} className="flex-1 overflow-y-auto scrollbar-thin px-2 pb-2 space-y-2">
            {(() => {
              const restInView = filtered.filter(c => !c.pinned);
              return restInView.slice(0, visibleCount).map(renderConv);
            })()}
          </div>
        </div>

        {/* Chat panel — masqué sur mobile tant qu'aucune conv n'est ouverte (on montre la liste). */}
        <div className={`flex-1 flex-col min-w-0 bg-[#0C0C0F] ${activeConv ? 'flex' : 'hidden md:flex'}`}>
          {conv ? (
            <>
              <div className="px-4 py-2.5 border-b border-white/5 flex items-center gap-3 shrink-0">
                <button data-testid="inbox-back-mobile" onClick={() => setActiveConv(null)} className="md:hidden -ml-1 p-1 text-slate-400 hover:text-white shrink-0" aria-label="Retour à la liste"><ChevronLeft size={22} /></button>
                <ConvAvatar name={conv.name} url={conv.profile_photo_url} size="w-9 h-9" textSize="text-xs" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white flex items-center gap-1.5">
                    {conv.name}
                    <button data-testid="rename-conv-button" onClick={() => setRenaming(conv)} className="text-slate-500 hover:text-white"><Pencil size={11} /></button>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {(conv.lists || []).map(lid => {
                      const l = allLists.find(x => x.id === lid);
                      if (!l) return null;
                      if (l.rule_field && l.rule_op) return null; // liste AUTO épinglée → affichée via les badges auto (pas de doublon)
                      const isManual = manualLists.some(x => x.id === lid);
                      return (
                        <span key={lid} data-testid={`header-list-chip-${lid}`} className={`text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1 ${l.color}`}>
                          {l.emoji || '●'} {l.name}
                          {isManual && <button data-testid={`remove-list-${lid}`} onClick={() => { const next = (conv.lists || []).filter(x => x !== lid); majLocale(conv.id, { lists: next }); patchConv(conv.id, { lists: next }); }} className="hover:opacity-70"><X size={9} /></button>}
                        </span>
                      );
                    })}
                    {/* Étiquettes AUTOMATIQUES (smart lists) — mêmes que la liste de gauche → synchro. NEW masqué (déjà un badge). */}
                    {(smartBadgesByConv[conv.id] || []).filter(sl => !/nouve|new/i.test(sl.name || '')).map(sl => (
                      <span key={`hs-${sl.id}`} title="Étiquette automatique" className={`text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1 ${sl.color || 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30'}`}>
                        {sl.emoji || '⚡'} {sl.name}
                      </span>
                    ))}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid="add-to-list-button" className="w-5 h-5 rounded-full bg-white/10 hover:bg-white/20 text-slate-300 flex items-center justify-center" title="Ajouter une étiquette (1 clic)"><Plus size={11} /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="bg-[#0B0B0E] border-white/10 text-slate-200 max-h-72 overflow-y-auto">
                        <DropdownMenuLabel className="text-[10px] text-slate-500">Étiquettes — clic = ajoute / retire</DropdownMenuLabel>
                        {manualLists.map(l => {
                          const on = (conv.lists || []).includes(l.id);
                          return (
                            <DropdownMenuItem key={l.id} data-testid={`quick-list-${l.id}`}
                              onSelect={(e) => {
                                e.preventDefault();
                                const cur = conv.lists || [];
                                const next = on ? cur.filter(x => x !== l.id) : [...cur, l.id];
                                majLocale(conv.id, { lists: next }); // instant
                                patchConv(conv.id, { lists: next });
                              }}
                              className="focus:bg-white/5 text-xs flex items-center gap-2 cursor-pointer">
                              <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-indigo-500 border-indigo-500' : 'border-white/25'}`}>{on && <Check size={10} className="text-white" />}</span>
                              <span className="truncate">{l.emoji || '●'} {l.name}</span>
                            </DropdownMenuItem>
                          );
                        })}
                        {manualLists.length === 0 && <div className="px-2 py-2 text-[11px] text-slate-500">Aucune étiquette manuelle</div>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                {conv.zone_bot && (
                  <div className="flex items-center gap-2 shrink-0" data-testid="zone-bot-entete">
                    <span className="text-[11px] text-violet-200 flex items-center gap-1" title="Le bot lui parle">
                      <span className="sm:hidden text-sm leading-none" data-testid="zone-bot-entete-court">🤖</span>
                      <span className="hidden sm:flex items-center gap-1"><Bot size={13} /> Le bot lui parle</span>
                    </span>
                    {canMoveBox && (
                      <button data-testid="rendre-aux-chatteurs" onClick={() => rendreAuxChatteurs(conv)} title="Rendre aux chatteurs"
                        className="h-7 px-2.5 rounded-full text-violet-100 border border-violet-400/50 bg-violet-500/20 text-[11px] font-semibold hover:bg-violet-500/30">
                        <span className="sm:hidden">Rendre</span><span className="hidden sm:inline">Rendre aux chatteurs</span>
                      </button>
                    )}
                  </div>
                )}
                {(() => { const k = classementBot(conv, false, derniereVenteParConv.get(conv.id) || 0); return k && (
                  <EtiquetteClassement c={conv} k={k} testid="bot-classement-entete" className={`shrink-0 text-[11px] font-semibold rounded-full px-2 py-0.5 border ${COULEUR_CLASSEMENT[k.classe]}`}
                    onRetirer={peutRetirerClassement ? retirerClassement : undefined} />
                ); })()}
                {!conv.zone_bot && botParleIds.has(conv.id) && (
                  <span className="shrink-0 text-[11px] text-amber-200 flex items-center gap-1" data-testid="bot-parle-entete" title="Le bot lui parle">
                    <span className="sm:hidden text-sm leading-none">🤖</span>
                    <span className="hidden sm:inline">🤖 le bot lui parle</span>
                  </span>
                )}
                {dernierLien && (
                  <button data-testid="goto-last-link" onClick={allerAuDernierLien}
                    title="Revenir au dernier lien de paiement envoyé"
                    className="hidden sm:flex items-center gap-1.5 h-7 px-2.5 rounded-full text-indigo-200 border border-indigo-500/40 bg-indigo-500/[0.14] text-[11px] font-semibold shrink-0 hover:bg-indigo-500/25">
                    <Zap size={12} /> Dernier lien · {fmtDateTimeParis(dernierLien.m.created_at).replace(/\/\d{4}/, '')}
                  </button>
                )}
                <button data-testid="open-insight-mobile" style={{ background: 'rgba(99,102,241,0.18)' }} className="xl:hidden flex items-center gap-1 h-7 px-2.5 rounded-full text-indigo-200 border border-indigo-500/40 text-[11px] font-semibold shrink-0" onClick={() => setShowInsight(true)} title="Notes & infos du fan"><FileText size={13} /> Notes</button>
                {hasPerm('manage_telegram') && (
                  <button data-testid="confier-bot-button" onClick={() => confierAuBot(conv)} title="Confier ce fan au bot (coché dans Telegram Business + ajouté à la liste du bot)"
                    className="hidden sm:flex items-center gap-1 h-7 px-2.5 rounded-full text-emerald-200 border border-emerald-500/40 bg-emerald-500/[0.12] text-[11px] font-semibold shrink-0 hover:bg-emerald-500/25">
                    🤖 Confier au bot
                  </button>
                )}
                {/* C-547 (04/11, Marlon : « un bouton quand je veux rendre un fan aux chatters », « Oui, à côté ») */}
                {hasPerm('manage_telegram') && conv.bot_hera && (
                  <button data-testid="rendre-chatteuses-button" onClick={() => rendreAuxChatteuses(conv)} title="Le bot arrête de lui écrire et le fan revient dans la messagerie des chatteuses"
                    className="hidden sm:flex items-center gap-1 h-7 px-2.5 rounded-full text-amber-200 border border-amber-500/40 bg-amber-500/[0.12] text-[11px] font-semibold shrink-0 hover:bg-amber-500/25">
                    👩 Rendre aux chatteuses
                  </button>
                )}
                {conv.blocked
                  ? peutDebloquer && <Button data-testid="unblock-conv-button" variant="ghost" size="sm" className="h-7 px-2 text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10 text-xs" onClick={() => unblockConv(conv)} title="Remettre dans la liste">Débloquer</Button>
                  : <Button data-testid="block-conv-button" variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10" onClick={() => blockConv(conv)} title="Bloquer"><Ban size={14} /></Button>}
                <Button data-testid="pin-conv-button" variant="ghost" size="icon" className={`h-7 w-7 hover:bg-white/5 ${conv.pinned ? 'text-amber-400' : 'text-slate-400'}`} onClick={() => togglePin(conv)} title={conv.pinned ? 'Désépingler' : 'Épingler en haut'}>
                  <Pin size={14} className={conv.pinned ? 'fill-amber-400' : ''} />
                </Button>
              </div>

              {/* Messages */}
              <div ref={brancherFil} onScroll={onMsgScroll} onWheel={noterGesteFil} onTouchStart={noterGesteFil} onTouchMove={noterGesteFil} onMouseDown={noterGesteFil} onKeyDown={noterGesteFil} className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-3" style={{ background: 'radial-gradient(ellipse at top, rgba(99,102,241,0.04), transparent 60%), #0C0C0F' }}>
                {messages.slice(-visibleMsgCount).map((m, i, arr) => {
                  // SÉPARATEUR DE DATE (04/09) : affiché quand le jour change — « aujourd'hui »,
                  // « hier », sinon « mercredi 27 août ». Sans ça, en remontant une longue
                  // conversation on ne savait plus de quel jour on lisait les messages.
                  const jour = fmtDayKeyParis(m.created_at);
                  const jourAvant = i > 0 ? fmtDayKeyParis(arr[i - 1].created_at) : null;
                  return (
                    <React.Fragment key={m.id}>
                      {jour && jour !== jourAvant && (
                        <div className="flex items-center gap-2 py-1" data-testid="date-separator">
                          <div className="flex-1 h-px bg-white/[0.07]" />
                          <span className="text-[11px] text-slate-400 bg-white/[0.06] rounded-full px-2.5 py-0.5">{fmtDayLabelParis(m.created_at)}</span>
                          <div className="flex-1 h-px bg-white/[0.07]" />
                        </div>
                      )}
                      <div id={`msg-${m.id}`} className={`rounded-2xl transition-colors duration-500 ${msgSurligne === m.id ? 'surlignage-lien' : ''}`}>
                        <MessageBubble m={m} translation={translations[m.id]} onRetry={() => retrySend(m)}
                          onReportSale={() => setReportMsg(m)} onReply={() => startReply(m)} onTranslate={() => translateMessage(m)}
                          onDelete={(id) => supprimerDuFil(id)}
                          onReacted={(id, reactions, myReaction) => setMessages(prev => prev.map(x => (x.id === id ? { ...x, reactions, my_reaction: myReaction } : x)))} />
                      </div>
                    </React.Fragment>
                  );
                })}
                {conv.seen_by && messages.length > 0 && messages[messages.length - 1].from_ === 'them' && (
                  <div className="flex justify-end">
                    <span className="text-[10px] text-slate-500 flex items-center gap-1" data-testid="seen-by-label"><CheckCheck size={11} className="text-sky-400" /> Vu par {conv.seen_by}</span>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              {/* Composer */}
              <div className="p-3 border-t border-white/5 shrink-0 space-y-2">
                {/* Anti-spam cooldown banner: Telegram PeerFlood-blocked the last send to this
                    fan. Shown BEFORE the chatter types, so they know it's Telegram limiting the
                    account — not them, not a ChatHub bug — and move on instead of retrying. */}
                {conv.peerflood_until && new Date(String(conv.peerflood_until).match(/Z$|[+-]\d{2}:?\d{2}$/) ? conv.peerflood_until : conv.peerflood_until + 'Z') > new Date() && (
                  <div data-testid="peerflood-banner" className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-200">
                    <span className="text-base leading-none">⏸️</span>
                    <span>
                      <b>Ce fan est en pause anti-spam</b> — Telegram a bloqué le dernier envoi vers lui (compte en limitation, ce n'est ni toi ni un bug).
                      Réessayer maintenant prolongerait la limitation : occupe-toi des fans qui t'écrivent, et reviens vers lui dans quelques heures.
                    </span>
                  </div>
                )}
                {(pendingScript || pendingVaults.length > 0 || replyTo) && (
                  <div className="flex gap-2 flex-wrap">
                    {replyTo && (
                      <span className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg bg-sky-500/15 border border-sky-500/30 text-sky-300" data-testid="reply-chip">
                        <ReplyIcon size={11} /> {replyTo.name}: {replyTo.text}
                        <button onClick={() => setReplyTo(null)}><X size={11} /></button>
                      </span>
                    )}
                    {pendingScript && (
                      <span className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg bg-indigo-500/15 border border-indigo-500/30 text-indigo-300" data-testid="pending-script-chip">
                        <FileText size={11} /> {pendingScript.title}
                        <button onClick={() => setPendingScript(null)}><X size={11} /></button>
                      </span>
                    )}
                    {pendingVaults.map(pv => (
                      <span key={pv.id} className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-300" data-testid={`pending-vault-chip-${pv.id}`}>
                        {pv.url && pv.type === 'photo' ? <img src={mediaUrl(pv.url)} alt="" className="w-5 h-5 rounded object-cover" /> : <FolderOpen size={11} />}
                        {pv.name}
                        <button onClick={() => setPendingVaults(prev => prev.filter(x => x.id !== pv.id))}><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div data-tour="inbox-actions" className="flex items-center gap-1.5 flex-wrap md:flex-nowrap md:overflow-x-auto scrollbar-thin">
                  {(account?.quick_emojis || []).map((em, i) => (
                    <button key={`${em}-${i}`} data-testid={`quick-emoji-${i}`} tabIndex={-1} onClick={() => { setDraft(d => d + em); inputRef.current?.focus(); }} className="w-7 h-7 rounded-lg hover:bg-white/10 text-base leading-none shrink-0">{em}</button>
                  ))}
                  <button data-testid="edit-emojis-button" onClick={() => setEmojiEdit(true)} className="w-7 h-7 rounded-lg hover:bg-white/10 text-slate-500 hover:text-white flex items-center justify-center shrink-0" title="Modifier les emojis rapides"><Pencil size={12} /></button>
                  <button data-testid="emoji-picker-button" onClick={() => setEmojiOpen((o) => !o)} className={`w-7 h-7 rounded-lg hover:bg-white/10 flex items-center justify-center shrink-0 text-base leading-none ${emojiOpen ? 'bg-white/10' : ''}`} title="Tous les emojis">😀</button>
                  {emojiOpen && (
                    <div data-testid="emoji-picker-panel" className="fixed bottom-[96px] left-1/2 -translate-x-1/2 w-[min(360px,92vw)] max-h-[280px] overflow-y-auto rounded-2xl border border-white/15 bg-[#0c0d14] shadow-2xl z-[60] p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-semibold text-slate-300">Tous les emojis</span>
                        <button onClick={() => setEmojiOpen(false)} className="text-slate-500 hover:text-white"><X size={14} /></button>
                      </div>
                      <div className="grid grid-cols-10 gap-1">
                        {EMOJI_BANK.map((em, i) => (
                          <button key={`pick-${em}-${i}`} onClick={() => { setDraft((d) => d + em); inputRef.current?.focus(); }} className="w-7 h-7 rounded-lg hover:bg-white/10 text-base leading-none">{em}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="hidden md:block flex-1" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button data-testid="translate-draft-button" variant="outline" size="sm" className="h-8 text-[11px] border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" disabled={translating}>
                        {translating ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Languages size={11} className="mr-1 text-sky-400" />} Traduire
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#0B0B0E] border-white/10 text-slate-200">
                      <DropdownMenuLabel className="text-[10px] text-slate-500">Traduire le brouillon en…</DropdownMenuLabel>
                      {LANGUAGES.map(l => <DropdownMenuItem key={l} data-testid={`translate-to-${l}`} onClick={() => translateDraft(l)} className="focus:bg-white/5 text-xs">{l}</DropdownMenuItem>)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button data-testid="open-scripts-picker" variant="outline" size="sm" className="h-7 text-[11px] border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={() => setScriptsOpen(true)}><FileText size={11} className="mr-1" /> Scripts</Button>
                  <Button data-testid="open-vault-picker" variant="outline" size="sm" className="h-7 text-[11px] border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={() => setVaultOpen(true)}><FolderOpen size={11} className="mr-1" /> Vault</Button>
                  <AiAssistant messages={messages} account={account} conversationId={conv?.id} onInsert={(t) => { setDraft(t); inputRef.current?.focus(); }} />
                </div>
                <div data-tour="inbox-composer" className="relative flex items-end gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
                  {slashOpen && (
                    <div className="absolute bottom-full left-0 right-0 mb-2 max-h-72 overflow-y-auto scrollbar-thin rounded-2xl border border-white/10 bg-[#0B0B0E] shadow-2xl z-50" data-testid="slash-menu">
                      <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5 sticky top-0 bg-[#0B0B0E]">/ Scripts · {slashMatches.length} — Entrée pour envoyer</div>
                      {slashMatches.map((s, i) => {
                        const sent = (conv?.sent_script_ids || []).includes(s.id);
                        const parBot = scriptsBienvenueBot.includes(s.id);
                        return (
                          <button key={s.id} type="button" data-testid={`slash-script-${s.id}`}
                            onMouseEnter={() => setSlashIdx(i)} onClick={() => sendSlashScript(s)}
                            className={`w-full text-left px-3 py-2 flex items-start gap-2 ${i === slashIdx ? 'bg-indigo-500/20' : 'hover:bg-white/5'} ${sent || parBot ? 'opacity-50' : ''}`}>
                            <FileText size={12} className="text-sky-400 mt-0.5 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-semibold text-white truncate">
                                {s.tag && (() => { const tc = scriptTags.find(t => t.name === s.tag)?.color || '#64748b'; return <span className="text-[9px] px-1.5 py-0.5 rounded mr-1.5 align-middle" style={{ background: `${tc}26`, color: tc }}>{s.tag}</span>; })()}
                                {s.title}
                                {s.price > 0 && <span className="text-[10px] text-emerald-300 ml-1">· {s.price}$</span>}
                                {(s.vault_ids || []).length > 0 && <span className="text-[10px] text-amber-300 ml-1">· 📎{(s.vault_ids || []).length}</span>}
                                {s.link && <span className="text-[10px] text-sky-300 ml-1">· 🔗</span>}
                                {sent && <span className="text-[9px] text-slate-400 ml-1">· déjà envoyé</span>}
                                {parBot && !sent && <span className="text-[9px] text-slate-400 ml-1" data-testid={`slash-bot-${s.id}`}>· déjà fait par le bot</span>}
                              </div>
                              <div className="text-[11px] text-slate-400 truncate">{s.body}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <textarea key={activeConv} ref={inputRef} data-testid="inbox-message-input" value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => {
                      if (slashOpen) {
                        if (e.key === 'ArrowDown') { e.preventDefault(); setSlashIdx(i => Math.min(i + 1, slashMatches.length - 1)); return; }
                        if (e.key === 'ArrowUp') { e.preventDefault(); setSlashIdx(i => Math.max(i - 1, 0)); return; }
                        if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') { e.preventDefault(); sendSlashScript(slashMatches[slashIdx]); return; }
                        if (e.key === 'Escape') { e.preventDefault(); setSlashDismissed(draft); return; }
                      }
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                    }}
                    placeholder="Écris un message… ( / pour les scripts · Shift+Entrée = saut de ligne )" rows={2}
                    className="flex-1 bg-transparent text-[15px] text-white placeholder:text-slate-500 outline-none resize-none py-1 min-h-[52px]" />
                  <Button data-testid="inbox-send-button" size="icon" className="h-10 w-10 bg-indigo-500 hover:bg-indigo-600 shrink-0 rounded-xl" onClick={send} disabled={sending}><Send size={16} /></Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">Sélectionne une conversation</div>
          )}
        </div>

        {/* Insight panel */}
        <div data-tour="inbox-insight" className="w-80 border-l border-white/5 hidden xl:flex flex-col shrink-0">
          {conv && (
            <InsightPanel conv={conv} account={account} sales={convSales}
              onPatch={(patch) => patchConv(conv.id, patch)} onPatchAccount={patchAccount} />
          )}
        </div>

        {/* Panneau Notes/infos fan — plein écran sur mobile/tablette (le panneau latéral est caché < xl) */}
        {conv && showInsight && (
          <div className="xl:hidden fixed inset-0 z-50 flex flex-col bg-[#0B0B0E]">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3 shrink-0">
              <button onClick={() => setShowInsight(false)} className="-ml-1 p-1 text-slate-400 hover:text-white" aria-label="Fermer"><ChevronLeft size={22} /></button>
              <div className="text-sm font-semibold text-white truncate">Notes & infos — {conv.name}</div>
              <button onClick={() => setShowInsight(false)} className="ml-auto p-1 text-slate-400 hover:text-white" aria-label="Fermer"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto scrollbar-thin">
              <InsightPanel conv={conv} account={account} sales={convSales}
                onPatch={(patch) => patchConv(conv.id, patch)} onPatchAccount={patchAccount} />
            </div>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <RenameDialog conv={renaming} onClose={() => setRenaming(null)} onSave={async (name) => { const r = await patchConv(renaming.id, { name }); if (r) toast.success('Conversation renommée'); setRenaming(null); }} />
      <EmojiEditDialog open={emojiEdit} account={account} onClose={() => setEmojiEdit(false)} onSave={async (emojis) => { const r = await patchAccount({ quick_emojis: emojis }); if (r) { toast.success('Emojis rapides mis à jour'); setEmojiEdit(false); } }} />
      <ScriptsPickerDialog open={scriptsOpen} onClose={() => setScriptsOpen(false)} scripts={scripts} tags={scriptTags} vault={vault} sentIds={conv?.sent_script_ids || []} paidIds={paidScriptIds} botIds={scriptsBienvenueBot} sentVaultIds={conv?.sent_vault_ids || []} onInsert={insertScript} />
      <VaultPickerDialog open={vaultOpen} onClose={() => setVaultOpen(false)} vault={vault} sentIds={conv?.sent_vault_ids || []} onPick={(vs) => { setPendingVaults(vs); setVaultOpen(false); }}
        onCreerLien={hasPerm('dropfans_send_paid') ? (vs) => { setLienMedias(vs); setVaultOpen(false); } : undefined} />
      <LienPayantDialog open={!!lienMedias} onClose={() => setLienMedias(null)} conv={conv} medias={lienMedias || []}
        onCree={(r) => {
          setLienMedias(null);
          setDraft(d => (d.trim() ? `${d.trimEnd()} ` : '') + r.url);
          toast.success(`Lien à ${Number(r.prix_eur).toLocaleString('fr-FR')} € créé : vérifie le message puis envoie-le`);
          setTimeout(() => inputRef.current?.focus(), 100);
        }} />
      <ReportSaleDialog msg={reportMsg} conv={conv} onClose={() => setReportMsg(null)} onReported={(sale) => { setConvSales(prev => [sale, ...prev]); setReportMsg(null); }} />
      <AddToListDialog open={addToList} conv={conv} smartLists={smartLists} manualLists={manualLists} onClose={() => setAddToList(false)}
        onSave={async (lists) => { await patchConv(conv.id, { lists }); }} />
    </div>
  );
}
